import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  doc, 
  deleteDoc,
  query,
  orderBy,
  where,
  writeBatch
} from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { Student, SchoolClass } from '../types';
import { getCachedStudents, getCachedClasses, dataCache, getCachedQuery } from '../services/dataCacheService';
import { useAcademicYear } from '../context/AcademicYearContext';
import { 
  getStudentClassForYear, 
  enrollStudentInYear, 
  unenrollStudentFromYear,
  getStudentTransportForYear,
  updateStudentTransportForYear,
  bulkAssignTransportForYear,
  normalizeClassName,
  isSameClass
} from '../services/academicYearService';
import { clearDailyStatsCache, refreshDailyStats } from '../services/statsService';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { safeHtml2Canvas } from '../utils/safeHtml2Canvas';
import { generateStudentReportPDF } from '../utils/studentPdfGenerator';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Check, 
  X, 
  Loader2, 
  Users, 
  Search, 
  UserPlus, 
  IdCard, 
  School, 
  Bus, 
  Calendar as CalendarIcon, 
  RefreshCw, 
  Printer, 
  FileDown,
  Calendar,
  AlertCircle,
  Sparkles,
  ArrowRightLeft,
  UserCheck,
  UserX,
  FileSpreadsheet,
  Upload,
  CheckSquare,
  Square,
  MinusSquare,
  Download,
  Info,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  SlidersHorizontal
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import BirthDatePicker from './BirthDatePicker';
import StudentAdvancedReportModal from './StudentAdvancedReportModal';

const StudentManagement: React.FC = () => {
  const { selectedYear, currentYearObj, academicYears } = useAcademicYear();
  const [students, setStudents] = useState<Student[]>([]);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClassFilter, setSelectedClassFilter] = useState('all');
  const [selectedTransportFilter, setSelectedTransportFilter] = useState('all');
  const [enrollmentStatusFilter, setEnrollmentStatusFilter] = useState<'all' | 'enrolled' | 'unenrolled' | 'inactive'>('all');
  const [showInactive, setShowInactive] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [savingStudentIds, setSavingStudentIds] = useState<Record<string, boolean>>({});
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [printingStudent, setPrintingStudent] = useState<Student | null>(null);
  const [isPrintingList, setIsPrintingList] = useState(false);
  const [showAdvancedReportModal, setShowAdvancedReportModal] = useState(false);

  // Sorting state for table columns
  const [sortField, setSortField] = useState<'fullName' | 'nationalId' | 'academicYear' | 'status' | 'class' | 'transport'>('fullName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: 'fullName' | 'nationalId' | 'academicYear' | 'status' | 'class' | 'transport') => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Bulk actions state
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [showBulkClassModal, setShowBulkClassModal] = useState(false);
  const [showBulkYearModal, setShowBulkYearModal] = useState(false);
  const [bulkTargetYearId, setBulkTargetYearId] = useState('');
  const [showBulkTransportModal, setShowBulkTransportModal] = useState(false);
  const [bulkTargetTransportName, setBulkTargetTransportName] = useState('');
  const [bulkManualTransportMode, setBulkManualTransportMode] = useState(false);
  const [bulkManualTransportName, setBulkManualTransportName] = useState('');
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkTargetClassId, setBulkTargetClassId] = useState('');
  const [bulkManualClassName, setBulkManualClassName] = useState('');
  const [bulkManualMode, setBulkManualMode] = useState(false);

  // Excel / CSV Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState<any[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importSuccessMsg, setImportSuccessMsg] = useState('');
  const [importErrorMsg, setImportErrorMsg] = useState('');
  const [importTargetYear, setImportTargetYear] = useState<string>(selectedYear);

  const [formData, setFormData] = useState({
    fullName: '',
    nationalId: '',
    birthDate: '',
    address: '',
    fatherName: '',
    fatherPhone: '',
    motherName: '',
    motherPhone: '',
    classId: '',
    transportName: '',
    escortName: '',
    healthNotes: '',
    enrollmentYear: '',
    isActive: true
  });
  const [manualClassMode, setManualClassMode] = useState(false);
  const [manualClassName, setManualClassName] = useState('');
  const [transportLines, setTransportLines] = useState<string[]>([]);

  useEffect(() => {
    fetchData();
    fetchTransportLines();
  }, []);

  const fetchTransportLines = async () => {
    try {
      const q = query(collection(db, 'transportLines'), orderBy('name'));
      const results = await getCachedQuery('transportLines_list', q);
      setTransportLines(results.map((doc: any) => doc.name));
    } catch (error) {
      console.error("Error fetching transport lines:", error);
    }
  };

  const fetchData = async (forceRefresh = false, silent = false) => {
    let safetyTimer: any = null;
    if (!silent) {
      setLoading(true);
      safetyTimer = setTimeout(() => {
        setLoading(false);
      }, 5000);
    }
    try {
      const classesData = await getCachedClasses(forceRefresh);
      const sortedClasses = (classesData || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he'));
      setClasses(sortedClasses);

      // Pass includeInactive = true to getCachedStudents so management shows all students
      const studentsData = await getCachedStudents(forceRefresh, undefined, true);
      setStudents(studentsData.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '', 'he')));
      setLastUpdated(new Date());
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'students');
    } finally {
      if (safetyTimer) clearTimeout(safetyTimer);
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const resolveClassIdAndName = async (
    isManual: boolean,
    manualName: string,
    selectedId: string
  ): Promise<{ classId: string; className: string } | null> => {
    if (isManual && manualName.trim()) {
      const trimmed = manualName.trim();
      const existing = classes.find(c => c.name.trim().toLowerCase() === trimmed.toLowerCase());
      if (existing) {
        return { classId: existing.id, className: existing.name };
      }
      try {
        const docRef = await addDoc(collection(db, 'classes'), {
          name: trimmed,
          color: '#4f46e5',
          active: true
        });
        dataCache.remove('classes');
        const newClassObj: SchoolClass = {
          id: docRef.id,
          name: trimmed,
          color: '#4f46e5',
          active: true
        };
        setClasses(prev => [...prev, newClassObj].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he')));
        return { classId: docRef.id, className: trimmed };
      } catch (err) {
        console.error('Error creating class:', err);
        return { classId: '', className: trimmed };
      }
    }

    if (selectedId && selectedId !== '__NONE__' && selectedId !== '__MANUAL__') {
      const existing = classes.find(c => c.id === selectedId);
      if (existing) {
        return { classId: existing.id, className: existing.name };
      }
      return { classId: selectedId, className: selectedId };
    }

    return null;
  };

  const handleAddStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.nationalId.trim()) {
      alert('נא להזין שם מלא ותעודת זהות');
      return;
    }

    if (formData.nationalId.length !== 9) {
      alert('תעודת זהות חייבת להכיל 9 ספרות');
      return;
    }

    const existingStudent = students.find(s => s.nationalId === formData.nationalId);
    if (existingStudent) {
      alert(`כבר קיים תלמיד עם תעודת זהות זו: ${existingStudent.fullName}`);
      return;
    }

    setActionLoading(true);
    try {
      const classInfo = await resolveClassIdAndName(manualClassMode, manualClassName, formData.classId);
      if (formData.isActive && !classInfo) {
        alert('אנא בחר כיתה מהרשימה או הקלד כיתה באופן ידני');
        setActionLoading(false);
        return;
      }

      const yearToEnroll = formData.enrollmentYear || selectedYear;
      const enrollmentMap = {
        [yearToEnroll]: {
          classId: classInfo ? classInfo.classId : '',
          className: classInfo ? classInfo.className : '',
          transportName: formData.transportName || '',
          escortName: formData.escortName || '',
          enrolledAt: new Date().toISOString(),
          active: formData.isActive
        }
      };

      const { enrollmentYear: _ey, isActive: _ia, ...baseFormData } = formData;

      await addDoc(collection(db, 'students'), {
        ...baseFormData,
        classId: classInfo ? classInfo.classId : '',
        className: classInfo ? classInfo.className : '',
        enrollments: enrollmentMap,
        active: formData.isActive,
        createdAt: new Date().toISOString()
      });
      dataCache.remove('students'); // Invalidate cache
      clearDailyStatsCache(yearToEnroll);
      const today = new Date().toISOString().split('T')[0];
      refreshDailyStats(today, yearToEnroll, true).catch(console.warn);
      setFormData({ 
        fullName: '', nationalId: '', birthDate: '', address: '', 
        fatherName: '', fatherPhone: '', motherName: '', motherPhone: '', 
        classId: '', transportName: '', escortName: '', healthNotes: '',
        enrollmentYear: '', isActive: true
      });
      setManualClassMode(false);
      setManualClassName('');
      setIsAdding(false);
      fetchData(true, true);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'students');
    } finally {
      setActionLoading(false);
    }
  };

  const openEditStudent = (s: Student) => {
    setEditingId(s.id);
    setIsAdding(false);
    const currentYearEnrollment = getStudentClassForYear(s, selectedYear);
    const rawEnrollment = s.enrollments?.[selectedYear];
    const effClassId = currentYearEnrollment?.classId || rawEnrollment?.classId || s.classId || '';
    const effClassName = currentYearEnrollment?.className || rawEnrollment?.className || s.className || '';
    const matched = classes.find(c => c.id === effClassId || c.name === effClassName);

    const transportInfo = getStudentTransportForYear(s, selectedYear);
    const effTransport = transportInfo.transportName || '';
    const effEscort = transportInfo.escortName || '';

    if (matched) {
      setFormData({
        fullName: s.fullName,
        nationalId: s.nationalId,
        birthDate: s.birthDate || '',
        address: s.address || '',
        fatherName: s.fatherName || '',
        fatherPhone: s.fatherPhone || '',
        motherName: s.motherName || '',
        motherPhone: s.motherPhone || '',
        classId: matched.id,
        transportName: effTransport,
        escortName: effEscort,
        healthNotes: s.healthNotes || '',
        enrollmentYear: selectedYear,
        isActive: rawEnrollment ? rawEnrollment.active !== false : true
      });
      setManualClassMode(false);
      setManualClassName('');
    } else if (effClassName) {
      setFormData({
        fullName: s.fullName,
        nationalId: s.nationalId,
        birthDate: s.birthDate || '',
        address: s.address || '',
        fatherName: s.fatherName || '',
        fatherPhone: s.fatherPhone || '',
        motherName: s.motherName || '',
        motherPhone: s.motherPhone || '',
        classId: '',
        transportName: effTransport,
        escortName: effEscort,
        healthNotes: s.healthNotes || '',
        enrollmentYear: selectedYear,
        isActive: rawEnrollment ? rawEnrollment.active !== false : true
      });
      setManualClassMode(true);
      setManualClassName(effClassName);
    } else {
      setFormData({
        fullName: s.fullName,
        nationalId: s.nationalId,
        birthDate: s.birthDate || '',
        address: s.address || '',
        fatherName: s.fatherName || '',
        fatherPhone: s.fatherPhone || '',
        motherName: s.motherName || '',
        motherPhone: s.motherPhone || '',
        classId: '',
        transportName: effTransport,
        escortName: effEscort,
        healthNotes: s.healthNotes || '',
        enrollmentYear: selectedYear,
        isActive: rawEnrollment ? rawEnrollment.active !== false : true
      });
      setManualClassMode(false);
      setManualClassName('');
    }
  };

  const handleUpdateStudent = async (id: string) => {
    if (!formData.fullName.trim() || !formData.nationalId.trim()) {
      alert('נא להזין שם מלא ותעודת זהות');
      return;
    }

    if (formData.nationalId.length !== 9) {
      alert('תעודת זהות חייבת להכיל 9 ספרות');
      return;
    }

    const existingStudent = students.find(s => s.nationalId === formData.nationalId && s.id !== id);
    if (existingStudent) {
      alert(`כבר קיים תלמיד אחר עם תעודת זהות זו: ${existingStudent.fullName}`);
      return;
    }

    const yearToUpdate = formData.enrollmentYear || selectedYear;

    setActionLoading(true);
    try {
      const classInfo = await resolveClassIdAndName(manualClassMode, manualClassName, formData.classId);
      const targetStudent = students.find(s => s.id === id);
      const existingEnrollments = targetStudent?.enrollments || {};
      const currentYearEnrollment = existingEnrollments[yearToUpdate];

      let updatedEnrollments = { ...existingEnrollments };

      if (formData.isActive) {
        if (classInfo) {
          updatedEnrollments[yearToUpdate] = {
            classId: classInfo.classId,
            className: classInfo.className,
            transportName: formData.transportName,
            escortName: formData.escortName,
            enrolledAt: currentYearEnrollment?.enrolledAt || new Date().toISOString(),
            active: true
          };
        } else if (currentYearEnrollment) {
          updatedEnrollments[yearToUpdate] = {
            ...currentYearEnrollment,
            transportName: formData.transportName,
            escortName: formData.escortName,
            active: true
          };
        } else {
          updatedEnrollments[yearToUpdate] = {
            classId: '',
            className: '',
            transportName: formData.transportName,
            escortName: formData.escortName,
            enrolledAt: new Date().toISOString(),
            active: true
          };
        }
      } else {
        if (currentYearEnrollment) {
          updatedEnrollments[yearToUpdate] = {
            ...currentYearEnrollment,
            classId: classInfo ? classInfo.classId : currentYearEnrollment.classId,
            className: classInfo ? classInfo.className : currentYearEnrollment.className,
            transportName: formData.transportName,
            escortName: formData.escortName,
            active: false
          };
        } else if (classInfo) {
          updatedEnrollments[yearToUpdate] = {
            classId: classInfo.classId,
            className: classInfo.className,
            transportName: formData.transportName,
            escortName: formData.escortName,
            enrolledAt: new Date().toISOString(),
            active: false
          };
        }
      }

      const { enrollmentYear: _ey, isActive: _ia, ...baseFormData } = formData;

      await updateDoc(doc(db, 'students', id), {
        ...baseFormData,
        active: formData.isActive,
        classId: classInfo ? classInfo.classId : (targetStudent?.classId || ''),
        className: classInfo ? classInfo.className : (targetStudent?.className || ''),
        enrollments: updatedEnrollments
      });
      dataCache.remove('students'); // Invalidate cache
      clearDailyStatsCache(yearToUpdate);
      const today = new Date().toISOString().split('T')[0];
      refreshDailyStats(today, yearToUpdate, true).catch(console.warn);
      setEditingId(null);
      setFormData({ 
        fullName: '', nationalId: '', birthDate: '', address: '', 
        fatherName: '', fatherPhone: '', motherName: '', motherPhone: '', 
        classId: '', transportName: '', escortName: '', healthNotes: '',
        enrollmentYear: '', isActive: true
      });
      setManualClassMode(false);
      setManualClassName('');
      fetchData(true, true);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'students');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async (student: Student, newStatus: string) => {
    const isTargetActive = newStatus === 'active';
    const enrollment = student.enrollments?.[selectedYear];
    const previousStudents = [...students];
    const scrollPos = window.scrollY;

    // Optimistic UI update: instantly update local state without unmounting or waiting
    setStudents(prev => prev.map(s => {
      if (s.id !== student.id) return s;
      const currentEnrollments = s.enrollments || {};
      const currentYearEnrollment = currentEnrollments[selectedYear] || {
        classId: s.classId || '',
        className: s.className || '',
        enrolledAt: new Date().toISOString()
      };
      return {
        ...s,
        enrollments: {
          ...currentEnrollments,
          [selectedYear]: {
            ...currentYearEnrollment,
            active: isTargetActive,
            ...(isTargetActive ? {} : { unenrolledAt: new Date().toISOString() })
          }
        }
      };
    }));

    setSavingStudentIds(prev => ({ ...prev, [student.id]: true }));

    try {
      if (!isTargetActive) {
        // Mark as inactive for this year
        await updateDoc(doc(db, 'students', student.id), {
          active: false
        });
        await unenrollStudentFromYear(student.id, selectedYear);
      } else {
        // Mark as active
        await updateDoc(doc(db, 'students', student.id), {
          active: true
        });
        if (enrollment) {
          await enrollStudentInYear(student.id, selectedYear, enrollment.classId, enrollment.className);
        } else if (student.classId) {
          const cls = classes.find(c => c.id === student.classId);
          await enrollStudentInYear(student.id, selectedYear, student.classId, cls?.name || student.className || '');
        } else {
          await enrollStudentInYear(student.id, selectedYear, '', '');
        }
      }
      dataCache.remove('students');
      clearDailyStatsCache(selectedYear);
      const today = new Date().toISOString().split('T')[0];
      refreshDailyStats(today, selectedYear, true).catch(console.warn);
      await fetchData(true, true);
    } catch (error) {
      console.error('Error changing student active status:', error);
      setStudents(previousStudents);
      alert('שגיאה בשינוי סטטוס תלמיד');
    } finally {
      setSavingStudentIds(prev => {
        const next = { ...prev };
        delete next[student.id];
        return next;
      });
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPos, behavior: 'instant' });
      });
    }
  };

  const handleAssignStudentToYear = async (student: Student, targetYearId: string) => {
    if (!targetYearId) return;
    const targetYearObj = academicYears.find(y => y.id === targetYearId);
    if (!targetYearObj) return;

    const previousStudents = [...students];
    const scrollPos = window.scrollY;

    const existingEnrollment = student.enrollments?.[targetYearId];
    let defaultClassId = existingEnrollment?.classId || '';
    let defaultClassName = existingEnrollment?.className || '';

    if (!defaultClassId && student.classId) {
      const matchedClass = classes.find(c => c.id === student.classId || c.name === student.className);
      if (matchedClass) {
        defaultClassId = matchedClass.id;
        defaultClassName = matchedClass.name;
      }
    }

    // Optimistic UI update
    setStudents(prev => prev.map(s => {
      if (s.id !== student.id) return s;
      return {
        ...s,
        enrollments: {
          ...(s.enrollments || {}),
          [targetYearId]: {
            classId: defaultClassId,
            className: defaultClassName,
            enrolledAt: existingEnrollment?.enrolledAt || new Date().toISOString(),
            active: true
          }
        }
      };
    }));

    setSavingStudentIds(prev => ({ ...prev, [student.id]: true }));

    try {
      await enrollStudentInYear(student.id, targetYearId, defaultClassId, defaultClassName);
      clearDailyStatsCache(targetYearId);
      clearDailyStatsCache(selectedYear);
      await fetchData(true, true);
    } catch (error) {
      console.error('Error assigning student to academic year:', error);
      setStudents(previousStudents);
      alert('שגיאה בשיוך תלמיד לשנת הלימודים');
    } finally {
      setSavingStudentIds(prev => {
        const next = { ...prev };
        delete next[student.id];
        return next;
      });
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPos, behavior: 'instant' });
      });
    }
  };

  const handleClassChangeInTable = async (student: Student, targetClassId: string) => {
    if (!targetClassId) return;

    if (targetClassId === '__MANUAL__') {
      const inputName = window.prompt(`הזן שם כיתה ידני עבור ${student.fullName}:`);
      if (!inputName || !inputName.trim()) return;
      const trimmed = inputName.trim();
      const scrollPos = window.scrollY;
      const previousStudents = [...students];

      setSavingStudentIds(prev => ({ ...prev, [student.id]: true }));
      try {
        let finalClassId = '';
        let finalClassName = trimmed;
        const matched = classes.find(c => c.name.trim().toLowerCase() === trimmed.toLowerCase());
        if (matched) {
          finalClassId = matched.id;
          finalClassName = matched.name;
        } else {
          const docRef = await addDoc(collection(db, 'classes'), {
            name: trimmed,
            color: '#4f46e5',
            active: true
          });
          finalClassId = docRef.id;
          dataCache.remove('classes');
          const newClassObj: SchoolClass = {
            id: docRef.id,
            name: trimmed,
            color: '#4f46e5',
            active: true
          };
          setClasses(prev => [...prev, newClassObj].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he')));
        }

        // Optimistic UI update
        setStudents(prev => prev.map(s => {
          if (s.id !== student.id) return s;
          return {
            ...s,
            enrollments: {
              ...(s.enrollments || {}),
              [selectedYear]: {
                ...(s.enrollments?.[selectedYear] || {}),
                classId: finalClassId,
                className: finalClassName,
                active: true,
                enrolledAt: s.enrollments?.[selectedYear]?.enrolledAt || new Date().toISOString()
              }
            }
          };
        }));

        await enrollStudentInYear(student.id, selectedYear, finalClassId, finalClassName);
        dataCache.remove('students');
        clearDailyStatsCache(selectedYear);
        const today = new Date().toISOString().split('T')[0];
        refreshDailyStats(today, selectedYear, true).catch(console.warn);
        await fetchData(true, true);
      } catch (err) {
        console.error('Error assigning manual class:', err);
        setStudents(previousStudents);
        alert('שגיאה בהזנת כיתה ידנית');
      } finally {
        setSavingStudentIds(prev => {
          const next = { ...prev };
          delete next[student.id];
          return next;
        });
        requestAnimationFrame(() => {
          window.scrollTo({ top: scrollPos, behavior: 'instant' });
        });
      }
      return;
    }

    if (targetClassId === '__NONE__') {
      const scrollPos = window.scrollY;
      const previousStudents = [...students];

      // Optimistic UI update
      setStudents(prev => prev.map(s => {
        if (s.id !== student.id) return s;
        return {
          ...s,
          enrollments: {
            ...(s.enrollments || {}),
            [selectedYear]: {
              ...(s.enrollments?.[selectedYear] || {}),
              classId: '',
              className: '',
              active: true,
              enrolledAt: s.enrollments?.[selectedYear]?.enrolledAt || new Date().toISOString()
            }
          }
        };
      }));

      setSavingStudentIds(prev => ({ ...prev, [student.id]: true }));
      try {
        await enrollStudentInYear(student.id, selectedYear, '', '');
        dataCache.remove('students');
        clearDailyStatsCache(selectedYear);
        const today = new Date().toISOString().split('T')[0];
        refreshDailyStats(today, selectedYear, true).catch(console.warn);
        await fetchData(true, true);
      } catch (err) {
        console.error('Error unassigning class:', err);
        setStudents(previousStudents);
        alert('שגיאה בביטול שיוך כיתה');
      } finally {
        setSavingStudentIds(prev => {
          const next = { ...prev };
          delete next[student.id];
          return next;
        });
        requestAnimationFrame(() => {
          window.scrollTo({ top: scrollPos, behavior: 'instant' });
        });
      }
      return;
    }

    const targetClass = classes.find(c => c.id === targetClassId);
    if (!targetClass) return;

    const scrollPos = window.scrollY;
    const previousStudents = [...students];

    // Optimistic UI update
    setStudents(prev => prev.map(s => {
      if (s.id !== student.id) return s;
      return {
        ...s,
        enrollments: {
          ...(s.enrollments || {}),
          [selectedYear]: {
            ...(s.enrollments?.[selectedYear] || {}),
            classId: targetClass.id,
            className: targetClass.name,
            active: true,
            enrolledAt: s.enrollments?.[selectedYear]?.enrolledAt || new Date().toISOString()
          }
        }
      };
    }));

    setSavingStudentIds(prev => ({ ...prev, [student.id]: true }));
    try {
      await enrollStudentInYear(student.id, selectedYear, targetClass.id, targetClass.name);
      dataCache.remove('students');
      clearDailyStatsCache(selectedYear);
      const today = new Date().toISOString().split('T')[0];
      refreshDailyStats(today, selectedYear, true).catch(console.warn);
      await fetchData(true, true);
    } catch (error) {
      console.error('Error changing student class:', error);
      setStudents(previousStudents);
      alert('שגיאה בשינוי כיתה');
    } finally {
      setSavingStudentIds(prev => {
        const next = { ...prev };
        delete next[student.id];
        return next;
      });
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPos, behavior: 'instant' });
      });
    }
  };

  const handleTransportChangeInTable = async (student: Student, targetTransport: string) => {
    if (!targetTransport) return;

    if (targetTransport === '__MANUAL__') {
      const inputName = window.prompt(`הזן שם קו הסעה / מסיע עבור ${student.fullName}:`);
      if (!inputName || !inputName.trim()) return;
      const trimmed = inputName.trim();
      const scrollPos = window.scrollY;
      const previousStudents = [...students];

      setSavingStudentIds(prev => ({ ...prev, [student.id]: true }));
      try {
        if (!transportLines.includes(trimmed)) {
          await addDoc(collection(db, 'transportLines'), {
            name: trimmed,
            createdAt: new Date().toISOString()
          });
          setTransportLines(prev => [...prev, trimmed].sort((a, b) => a.localeCompare(b, 'he')));
          dataCache.remove('transportLines_list');
        }

        // Optimistic UI update
        setStudents(prev => prev.map(s => {
          if (s.id !== student.id) return s;
          const prevEnrollments = s.enrollments || {};
          const prevYearEnrollment = prevEnrollments[selectedYear] || { classId: s.classId, className: s.className, active: true };
          return {
            ...s,
            transportName: trimmed,
            enrollments: {
              ...prevEnrollments,
              [selectedYear]: {
                ...prevYearEnrollment,
                transportName: trimmed
              }
            }
          };
        }));

        await updateStudentTransportForYear(student.id, selectedYear, trimmed);
        await fetchData(true, true);
      } catch (err) {
        console.error('Error assigning manual transport:', err);
        setStudents(previousStudents);
        alert('שגיאה בהזנת מסיע ידני');
      } finally {
        setSavingStudentIds(prev => {
          const next = { ...prev };
          delete next[student.id];
          return next;
        });
        requestAnimationFrame(() => {
          window.scrollTo({ top: scrollPos, behavior: 'instant' });
        });
      }
      return;
    }

    const finalTransport = targetTransport === '__NONE__' ? '' : targetTransport;
    const scrollPos = window.scrollY;
    const previousStudents = [...students];

    // Optimistic UI update
    setStudents(prev => prev.map(s => {
      if (s.id !== student.id) return s;
      const prevEnrollments = s.enrollments || {};
      const prevYearEnrollment = prevEnrollments[selectedYear] || { classId: s.classId, className: s.className, active: true };
      return {
        ...s,
        transportName: finalTransport,
        enrollments: {
          ...prevEnrollments,
          [selectedYear]: {
            ...prevYearEnrollment,
            transportName: finalTransport
          }
        }
      };
    }));

    setSavingStudentIds(prev => ({ ...prev, [student.id]: true }));
    try {
      await updateStudentTransportForYear(student.id, selectedYear, finalTransport);
      await fetchData(true, true);
    } catch (error) {
      console.error('Error changing student transport:', error);
      setStudents(previousStudents);
      alert('שגיאה בעדכון מסיע');
    } finally {
      setSavingStudentIds(prev => {
        const next = { ...prev };
        delete next[student.id];
        return next;
      });
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPos, behavior: 'instant' });
      });
    }
  };

  const handleQuickEnroll = async (studentId: string, classId: string) => {
    const targetClass = classes.find(c => c.id === classId);
    if (!targetClass) return;
    const scrollPos = window.scrollY;
    const previousStudents = [...students];

    setStudents(prev => prev.map(s => {
      if (s.id !== studentId) return s;
      return {
        ...s,
        enrollments: {
          ...(s.enrollments || {}),
          [selectedYear]: {
            ...(s.enrollments?.[selectedYear] || {}),
            classId: targetClass.id,
            className: targetClass.name,
            active: true,
            enrolledAt: s.enrollments?.[selectedYear]?.enrolledAt || new Date().toISOString()
          }
        }
      };
    }));

    setSavingStudentIds(prev => ({ ...prev, [studentId]: true }));
    try {
      await enrollStudentInYear(studentId, selectedYear, targetClass.id, targetClass.name);
      dataCache.remove('students');
      clearDailyStatsCache(selectedYear);
      const today = new Date().toISOString().split('T')[0];
      refreshDailyStats(today, selectedYear, true).catch(console.warn);
      await fetchData(true, true);
    } catch (error) {
      console.error('Error quick enrolling student:', error);
      setStudents(previousStudents);
      alert('שגיאה בשיבוץ תלמיד');
    } finally {
      setSavingStudentIds(prev => {
        const next = { ...prev };
        delete next[studentId];
        return next;
      });
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPos, behavior: 'instant' });
      });
    }
  };

  const handleDeleteStudent = async (id: string) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק תלמיד זה?')) return;
    const scrollPos = window.scrollY;
    const previousStudents = [...students];
    setStudents(prev => prev.filter(s => s.id !== id));
    setSavingStudentIds(prev => ({ ...prev, [id]: true }));
    try {
      await deleteDoc(doc(db, 'students', id));
      dataCache.remove('students'); // Invalidate cache
      clearDailyStatsCache(selectedYear);
      const today = new Date().toISOString().split('T')[0];
      refreshDailyStats(today, selectedYear, true).catch(console.warn);
      await fetchData(true, true);
    } catch (error) {
      setStudents(previousStudents);
      handleFirestoreError(error, OperationType.DELETE, 'students');
    } finally {
      setSavingStudentIds(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPos, behavior: 'instant' });
      });
    }
  };

  const handleDeleteAllStudents = async () => {
    if (students.length === 0) return;
    setActionLoading(true);
    try {
      const deletePromises = students.map(s => deleteDoc(doc(db, 'students', s.id)));
      await Promise.all(deletePromises);
      dataCache.remove('students'); // Invalidate cache
      clearDailyStatsCache(selectedYear);
      const today = new Date().toISOString().split('T')[0];
      refreshDailyStats(today, selectedYear, true).catch(console.warn);
      fetchData(true);
      setShowDeleteAllConfirm(false);
      setSelectedStudentIds([]);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'students');
    } finally {
      setActionLoading(false);
    }
  };

  // --- Bulk Selection & Batch Actions ---
  const toggleSelectStudent = (id: string) => {
    setSelectedStudentIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredStudents.map(s => s.id);
    const allSelected = filteredIds.length > 0 && filteredIds.every(id => selectedStudentIds.includes(id));
    if (allSelected) {
      setSelectedStudentIds(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedStudentIds(prev => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const handleBulkStatusChange = async (targetActive: boolean) => {
    if (selectedStudentIds.length === 0) return;
    const scrollPos = window.scrollY;
    setActionLoading(true);
    try {
      const batchSize = 400;
      for (let i = 0; i < selectedStudentIds.length; i += batchSize) {
        const chunk = selectedStudentIds.slice(i, i + batchSize);
        const batch = writeBatch(db);
        chunk.forEach(studentId => {
          const st = students.find(s => s.id === studentId);
          if (!st) return;
          const docRef = doc(db, 'students', studentId);
          const currentYearEnrollment = st.enrollments?.[selectedYear];
          
          if (targetActive) {
            batch.set(docRef, {
              active: true,
              enrollments: {
                [selectedYear]: {
                  classId: currentYearEnrollment?.classId || st.classId || '',
                  className: currentYearEnrollment?.className || st.className || '',
                  active: true,
                  enrolledAt: currentYearEnrollment?.enrolledAt || new Date().toISOString()
                }
              }
            }, { merge: true });
          } else {
            batch.set(docRef, {
              active: false,
              enrollments: {
                [selectedYear]: {
                  classId: currentYearEnrollment?.classId || '',
                  className: currentYearEnrollment?.className || '',
                  active: false,
                  unenrolledAt: new Date().toISOString()
                }
              }
            }, { merge: true });
          }
        });
        await batch.commit();
      }
      dataCache.remove('students');
      clearDailyStatsCache(selectedYear);
      const today = new Date().toISOString().split('T')[0];
      refreshDailyStats(today, selectedYear, true).catch(console.warn);
      await fetchData(true, true);
      alert(`סטטוס של ${selectedStudentIds.length} תלמידים עודכן בהצלחה ל-${targetActive ? 'פעיל' : 'לא פעיל'} לשנת ${currentYearObj.name}`);
    } catch (err) {
      console.error('Error in bulk status change:', err);
      alert('שגיאה בעדכון גורף של סטטוס התלמידים');
    } finally {
      setActionLoading(false);
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPos, behavior: 'instant' });
      });
    }
  };

  const handleBulkClassApply = async () => {
    if (selectedStudentIds.length === 0) return;
    const scrollPos = window.scrollY;
    
    setActionLoading(true);
    try {
      let finalClassId = '';
      let finalClassName = '';

      if (bulkManualMode && bulkManualClassName.trim()) {
        const trimmed = bulkManualClassName.trim();
        const matched = classes.find(c => c.name.trim().toLowerCase() === trimmed.toLowerCase());
        if (matched) {
          finalClassId = matched.id;
          finalClassName = matched.name;
        } else {
          const docRef = await addDoc(collection(db, 'classes'), {
            name: trimmed,
            color: '#4f46e5',
            active: true
          });
          finalClassId = docRef.id;
          dataCache.remove('classes');
          const newClassObj: SchoolClass = {
            id: docRef.id,
            name: trimmed,
            color: '#4f46e5',
            active: true
          };
          setClasses(prev => [...prev, newClassObj].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he')));
          finalClassName = trimmed;
        }
      } else if (bulkTargetClassId && bulkTargetClassId !== '__NONE__') {
        const cls = classes.find(c => c.id === bulkTargetClassId);
        if (cls) {
          finalClassId = cls.id;
          finalClassName = cls.name;
        }
      }

      const batchSize = 400;
      for (let i = 0; i < selectedStudentIds.length; i += batchSize) {
        const chunk = selectedStudentIds.slice(i, i + batchSize);
        const batch = writeBatch(db);
        chunk.forEach(studentId => {
          const docRef = doc(db, 'students', studentId);
          batch.set(docRef, {
            classId: finalClassId,
            className: finalClassName,
            active: true,
            enrollments: {
              [selectedYear]: {
                classId: finalClassId,
                className: finalClassName,
                active: true,
                enrolledAt: new Date().toISOString()
              }
            }
          }, { merge: true });
        });
        await batch.commit();
      }

      dataCache.remove('students');
      clearDailyStatsCache(selectedYear);
      const today = new Date().toISOString().split('T')[0];
      refreshDailyStats(today, selectedYear, true).catch(console.warn);
      await fetchData(true, true);
      setShowBulkClassModal(false);
      setBulkTargetClassId('');
      setBulkManualClassName('');
      setBulkManualMode(false);
      alert(`שויכו ${selectedStudentIds.length} תלמידים לכיתה ${finalClassName || 'ללא כיתה'} בהצלחה!`);
    } catch (err) {
      console.error('Error applying bulk class:', err);
      alert('שגיאה בשינוי כיתה גורף');
    } finally {
      setActionLoading(false);
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPos, behavior: 'instant' });
      });
    }
  };

  const handleBulkTransportApply = async () => {
    if (selectedStudentIds.length === 0) return;
    const scrollPos = window.scrollY;

    setActionLoading(true);
    try {
      let finalTransportName = '';
      if (bulkManualTransportMode) {
        if (!bulkManualTransportName.trim()) {
          alert('נא להזין שם קו הסעה / מסיע');
          setActionLoading(false);
          return;
        }
        finalTransportName = bulkManualTransportName.trim();
        if (!transportLines.includes(finalTransportName)) {
          await addDoc(collection(db, 'transportLines'), {
            name: finalTransportName,
            createdAt: new Date().toISOString()
          });
          setTransportLines(prev => [...prev, finalTransportName].sort((a, b) => a.localeCompare(b, 'he')));
          dataCache.remove('transportLines_list');
        }
      } else {
        if (bulkTargetTransportName === '__NONE__') {
          finalTransportName = '';
        } else {
          finalTransportName = bulkTargetTransportName;
        }
      }

      // Optimistic UI update
      setStudents(prev => prev.map(s => {
        if (!selectedStudentIds.includes(s.id)) return s;
        const prevEnrollments = s.enrollments || {};
        const prevYearEnrollment = prevEnrollments[selectedYear] || { classId: s.classId, className: s.className, active: true };
        return {
          ...s,
          transportName: finalTransportName,
          enrollments: {
            ...prevEnrollments,
            [selectedYear]: {
              ...prevYearEnrollment,
              transportName: finalTransportName
            }
          }
        };
      }));

      await bulkAssignTransportForYear(selectedStudentIds, selectedYear, finalTransportName);

      setSelectedStudentIds([]);
      setShowBulkTransportModal(false);
      setBulkTargetTransportName('');
      setBulkManualTransportName('');
      setBulkManualTransportMode(false);
      await fetchData(true, true);
    } catch (error) {
      console.error('Error applying bulk transport:', error);
      alert('שגיאה בשיוך גורף למסיע');
    } finally {
      setActionLoading(false);
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPos, behavior: 'instant' });
      });
    }
  };

  const handleBulkYearAssign = async () => {
    if (selectedStudentIds.length === 0 || !bulkTargetYearId) return;
    const targetYearObj = academicYears.find(y => y.id === bulkTargetYearId);
    if (!targetYearObj) return;

    const scrollPos = window.scrollY;
    setActionLoading(true);
    try {
      const batchSize = 400;
      for (let i = 0; i < selectedStudentIds.length; i += batchSize) {
        const chunk = selectedStudentIds.slice(i, i + batchSize);
        const batch = writeBatch(db);
        chunk.forEach(studentId => {
          const st = students.find(s => s.id === studentId);
          if (!st) return;

          const existingEnrollment = st.enrollments?.[bulkTargetYearId];
          let assignedClassId = existingEnrollment?.classId || '';
          let assignedClassName = existingEnrollment?.className || '';

          if (!assignedClassId && st.classId) {
            const matchedClass = classes.find(c => c.id === st.classId || c.name === st.className);
            if (matchedClass) {
              assignedClassId = matchedClass.id;
              assignedClassName = matchedClass.name;
            }
          }

          const docRef = doc(db, 'students', studentId);
          batch.set(docRef, {
            enrollments: {
              [bulkTargetYearId]: {
                classId: assignedClassId,
                className: assignedClassName,
                active: true,
                enrolledAt: existingEnrollment?.enrolledAt || new Date().toISOString()
              }
            }
          }, { merge: true });
        });
        await batch.commit();
      }

      dataCache.remove('students');
      clearDailyStatsCache(bulkTargetYearId);
      clearDailyStatsCache(selectedYear);
      await fetchData(true, true);
      setShowBulkYearModal(false);
      setBulkTargetYearId('');
      alert(`שויכו ${selectedStudentIds.length} תלמידים לשנת הלימודים ${targetYearObj.name} בהצלחה!`);
    } catch (err) {
      console.error('Error applying bulk year assignment:', err);
      alert('שגיאה בשיוך גורף לשנת לימודים');
    } finally {
      setActionLoading(false);
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPos, behavior: 'instant' });
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedStudentIds.length === 0) return;
    const scrollPos = window.scrollY;
    setActionLoading(true);
    try {
      const batchSize = 400;
      for (let i = 0; i < selectedStudentIds.length; i += batchSize) {
        const chunk = selectedStudentIds.slice(i, i + batchSize);
        const batch = writeBatch(db);
        chunk.forEach(studentId => {
          const docRef = doc(db, 'students', studentId);
          batch.delete(docRef);
        });
        await batch.commit();
      }
      dataCache.remove('students');
      clearDailyStatsCache(selectedYear);
      const today = new Date().toISOString().split('T')[0];
      refreshDailyStats(today, selectedYear, true).catch(console.warn);
      await fetchData(true, true);
      setShowBulkDeleteModal(false);
      setSelectedStudentIds([]);
      alert(`נמחקו בהצלחה ${selectedStudentIds.length} תלמידים שנבחרו.`);
    } catch (err) {
      console.error('Error bulk deleting students:', err);
      alert('שגיאה במחיקת התלמידים הנבחרים');
    } finally {
      setActionLoading(false);
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPos, behavior: 'instant' });
      });
    }
  };

  // --- Excel / CSV Template & Import ---
  const downloadExcelTemplate = () => {
    const templateData = [
      {
        'שם מלא': 'ישראל ישראלי',
        'תעודת זהות': '123456789',
        'כיתה': classes[0]?.name || 'אורנים',
        'תאריך לידה': '2019-05-15',
        'כתובת': 'רחוב הרצל 1, עכו',
        'שם אבא': 'אברהם',
        'נייד אבא': '0501234567',
        'שם אמא': 'שרה',
        'נייד אמא': '0529876543',
        'מסיע': transportLines[0] || 'קו 1',
        'מלווה': 'מרים',
        'הערות בריאותיות': 'רגישות לבוטנים'
      },
      {
        'שם מלא': 'יוסף כהן',
        'תעודת זהות': '987654321',
        'כיתה': classes[1]?.name || classes[0]?.name || 'ברושים',
        'תאריך לידה': '2020-02-10',
        'כתובת': 'רחוב הגליל 4, כרמיאל',
        'שם אבא': 'יעקב',
        'נייד אבא': '0541112233',
        'שם אמא': 'רחל',
        'נייד אמא': '0544445566',
        'מסיע': transportLines[1] || transportLines[0] || 'קו 2',
        'מלווה': 'שרה',
        'הערות בריאותיות': ''
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'תלמידים');
    XLSX.writeFile(workbook, 'תבנית_יבוא_תלמידים.xlsx');
  };

  const handleExcelFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    setImportErrorMsg('');
    setImportSuccessMsg('');

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsName = wb.SheetNames[0];
        const ws = wb.Sheets[wsName];
        const rawJson: any[] = XLSX.utils.sheet_to_json(ws, { defval: '' });

        if (!rawJson || rawJson.length === 0) {
          setImportErrorMsg('קובץ האקסל ריק או שאינו מכיל נתונים');
          setImportLoading(false);
          return;
        }

        const mapped = rawJson.map((row: any) => {
          const findVal = (keys: string[]) => {
            const foundKey = Object.keys(row).find(k => 
              keys.some(key => k.trim().toLowerCase().includes(key.toLowerCase()) || key.toLowerCase().includes(k.trim().toLowerCase()))
            );
            return foundKey ? String(row[foundKey]).trim() : '';
          };

          const rawId = findVal(['תעודת זהות', 'ת"ז', 'ת.ז', 'זהות', 'nationalid', 'id']);
          // Pad 9 digits if necessary
          const cleanId = rawId ? rawId.padStart(9, '0') : '';

          return {
            fullName: findVal(['שם מלא', 'שם תלמיד', 'שם', 'fullname', 'name']),
            nationalId: cleanId,
            birthDate: findVal(['תאריך לידה', 'ת"ל', 'ת.ל', 'birthdate', 'birth']),
            address: findVal(['כתובת', 'address', 'ישוב', 'עיר']),
            fatherName: findVal(['שם אבא', 'אבא', 'fathername', 'father']),
            fatherPhone: findVal(['נייד אבא', 'טלפון אבא', 'נייד אב', 'fatherphone']),
            motherName: findVal(['שם אמא', 'אמא', 'mothername', 'mother']),
            motherPhone: findVal(['נייד אמא', 'טלפון אמא', 'נייד אם', 'motherphone']),
            className: findVal(['כיתה', 'classname', 'גן']),
            transportName: findVal(['מסיע', 'קו', 'הסעה', 'transportname']),
            escortName: findVal(['מלווה', 'escortname']),
            healthNotes: findVal(['הערות בריאותיות', 'הערות', 'healthnotes', 'בריאות'])
          };
        }).filter(item => item.fullName || item.nationalId);

        if (mapped.length === 0) {
          setImportErrorMsg('לא נמצאו תלמידים תקינים עם שם מלא או ת"ז בקובץ');
        } else {
          setImportPreviewData(mapped);
        }
      } catch (err) {
        console.error('Error reading excel file:', err);
        setImportErrorMsg('שגיאה בקריאת קובץ האקסל. וודא שהקובץ תקין בפורמט xlsx או xls');
      } finally {
        setImportLoading(false);
      }
    };

    reader.onerror = () => {
      setImportErrorMsg('קריאת הקובץ נכשלה');
      setImportLoading(false);
    };

    reader.readAsBinaryString(file);
  };

  const handleExecuteImport = async () => {
    if (importPreviewData.length === 0) return;
    setImportLoading(true);
    setImportErrorMsg('');

    try {
      const yearToImport = importTargetYear || selectedYear;
      const classMap: Record<string, SchoolClass> = {};
      classes.forEach(c => {
        classMap[c.name.trim().toLowerCase()] = c;
      });

      // Prepare newly created classes if any
      const newClassNames: string[] = Array.from(new Set(
        importPreviewData
          .map(r => (r.className ? String(r.className).trim() : ''))
          .filter((cn: string) => Boolean(cn) && !classMap[cn.toLowerCase()])
      ));

      for (const clsName of newClassNames) {
        const docRef = await addDoc(collection(db, 'classes'), {
          name: clsName,
          color: '#4f46e5',
          active: true
        });
        const newCls: SchoolClass = {
          id: docRef.id,
          name: clsName,
          color: '#4f46e5',
          active: true
        };
        classMap[clsName.toLowerCase()] = newCls;
      }
      if (newClassNames.length > 0) {
        dataCache.remove('classes');
      }

      // Existing students indexed by nationalId
      const studentMapByNatId: Record<string, Student> = {};
      students.forEach(s => {
        if (s.nationalId) studentMapByNatId[s.nationalId.trim()] = s;
      });

      let addedCount = 0;
      let updatedCount = 0;

      const batchSize = 400;
      for (let i = 0; i < importPreviewData.length; i += batchSize) {
        const chunk = importPreviewData.slice(i, i + batchSize);
        const batch = writeBatch(db);

        chunk.forEach(row => {
          const targetCls = row.className ? classMap[row.className.toLowerCase()] : null;
          const clsId = targetCls?.id || '';
          const clsName = targetCls?.name || row.className || '';

          const enrollmentObj = {
            classId: clsId,
            className: clsName,
            enrolledAt: new Date().toISOString(),
            active: true
          };

          const existingStudent = row.nationalId ? studentMapByNatId[row.nationalId.trim()] : null;

          if (existingStudent) {
            const studentRef = doc(db, 'students', existingStudent.id);
            batch.update(studentRef, {
              fullName: row.fullName || existingStudent.fullName,
              birthDate: row.birthDate || existingStudent.birthDate || '',
              address: row.address || existingStudent.address || '',
              fatherName: row.fatherName || existingStudent.fatherName || '',
              fatherPhone: row.fatherPhone || existingStudent.fatherPhone || '',
              motherName: row.motherName || existingStudent.motherName || '',
              motherPhone: row.motherPhone || existingStudent.motherPhone || '',
              transportName: row.transportName || existingStudent.transportName || '',
              escortName: row.escortName || existingStudent.escortName || '',
              healthNotes: row.healthNotes || existingStudent.healthNotes || '',
              [`enrollments.${yearToImport}`]: enrollmentObj
            });
            updatedCount++;
          } else {
            const newRef = doc(collection(db, 'students'));
            batch.set(newRef, {
              fullName: row.fullName,
              nationalId: row.nationalId || '',
              birthDate: row.birthDate || '',
              address: row.address || '',
              fatherName: row.fatherName || '',
              fatherPhone: row.fatherPhone || '',
              motherName: row.motherName || '',
              motherPhone: row.motherPhone || '',
              transportName: row.transportName || '',
              escortName: row.escortName || '',
              healthNotes: row.healthNotes || '',
              classId: clsId,
              className: clsName,
              active: true,
              createdAt: new Date().toISOString(),
              enrollments: {
                [yearToImport]: enrollmentObj
              }
            });
            addedCount++;
          }
        });

        await batch.commit();
      }

      dataCache.remove('students');
      clearDailyStatsCache(yearToImport);
      const today = new Date().toISOString().split('T')[0];
      refreshDailyStats(today, yearToImport, true).catch(console.warn);
      await fetchData(true);
      setImportSuccessMsg(`היבוא הושלם בהצלחה! נוספו ${addedCount} תלמידים חדשים, ועודכנו ${updatedCount} תלמידים קיימים לשנת ${currentYearObj.name}.`);
      setImportPreviewData([]);
    } catch (err) {
      console.error('Error importing students from excel:', err);
      setImportErrorMsg('שגיאה ביבוא הנתונים. אנא נסה שוב.');
    } finally {
      setImportLoading(false);
    }
  };

  const handlePrint = () => {
    const element = document.getElementById('printable-student-card');
    if (!element) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('נא לאפשר חלונות קופצים בדפדפן כדי להדפיס');
      return;
    }

    const cardHtml = `
      <div style="border: 1px solid #e2e8f0; border-radius: 12px; padding: 30px; max-width: 800px; margin: 0 auto; direction: rtl;">
        <div style="display: flex; justify-content: space-between; border-bottom: 2px solid #f1f5f9; padding-bottom: 15px; margin-bottom: 20px;">
          <div>
            <h1 style="font-size: 24px; margin: 0;">כרטיס תלמיד</h1>
            <p style="color: #4f46e5; font-weight: bold; font-size: 20px; margin: 5px 0;">${printingStudent?.fullName}</p>
          </div>
          <img src="/logo.png" style="height: 60px;" />
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
          <div><div style="font-size: 11px; color: #94a3b8; font-weight: bold;">תעודת זהות</div><div style="font-size: 16px; font-weight: 600;">${printingStudent?.nationalId}</div></div>
          <div><div style="font-size: 11px; color: #94a3b8; font-weight: bold;">כיתה</div><div style="font-size: 16px; font-weight: 600;">${printingStudent?.className}</div></div>
          <div><div style="font-size: 11px; color: #94a3b8; font-weight: bold;">תאריך לידה</div><div style="font-size: 16px;">${printingStudent?.birthDate || '-'}</div></div>
          <div><div style="font-size: 11px; color: #94a3b8; font-weight: bold;">הסעה</div><div style="font-size: 16px;">${printingStudent?.transportName || '-'}</div></div>
          <div style="grid-column: span 2;"><div style="font-size: 11px; color: #94a3b8; font-weight: bold;">כתובת</div><div style="font-size: 16px;">${printingStudent?.address || '-'}</div></div>
        </div>
        <div style="margin-top: 20px; background: #f8fafc; padding: 15px; border-radius: 8px;">
          <div style="font-size: 11px; color: #94a3b8; font-weight: bold;">הערות רפואיות</div>
          <div style="font-size: 14px;">${printingStudent?.healthNotes || 'אין הערות'}</div>
        </div>
      </div>
    `;

    printWindow.document.write(`
      <html dir="rtl">
        <head>
          <title>הדפסת כרטיס תלמיד - ${printingStudent?.fullName}</title>
          <style>
            body { font-family: sans-serif; padding: 40px; background: white; }
          </style>
        </head>
        <body>
          ${cardHtml}
          <script>
            window.onload = () => {
              setTimeout(() => { window.print(); window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handlePrintList = () => {
    setIsPrintingList(true);
    
    setTimeout(() => {
      const element = document.getElementById('printable-student-list');
      if (!element) {
        setIsPrintingList(false);
        return;
      }

      const studentsByClass: Record<string, Student[]> = {};
      filteredStudents.forEach(s => {
        if (!studentsByClass[s.className]) studentsByClass[s.className] = [];
        studentsByClass[s.className].push(s);
      });

      const classesToPrint = Object.keys(studentsByClass).sort();

      let pagesHtml = '';
      classesToPrint.forEach((className, index) => {
        const classStudents = studentsByClass[className];
        pagesHtml += `
          <div style="${index > 0 ? 'page-break-before: always; margin-top: 40px;' : 'padding: 20px;'}">
            <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px; margin-bottom: 20px; direction: rtl;">
              <div style="text-align: right;">
                <h2 style="margin: 0; font-size: 22px;">רשימת תלמידים - כיתה ${className}</h2>
                <p style="color: #64748b; margin: 5px 0; font-size: 14px;">תאריך הדפסה: ${new Date().toLocaleDateString('he-IL')}</p>
                <p style="color: #64748b; margin: 0; font-size: 14px;">סה"כ תלמידים: ${classStudents.length}</p>
              </div>
              <img src="/logo.png" style="height: 50px;" />
            </div>
            <table style="width: 100%; border-collapse: collapse; direction: rtl;">
              <thead>
                <tr style="background-color: #f8fafc;">
                  <th style="border: 1px solid #cbd5e1; padding: 10px; text-align: center; width: 40px;">#</th>
                  <th style="border: 1px solid #cbd5e1; padding: 10px; text-align: right;">שם מלא</th>
                  <th style="border: 1px solid #cbd5e1; padding: 10px; text-align: right;">תעודת זהות</th>
                  <th style="border: 1px solid #cbd5e1; padding: 10px; text-align: right;">טלפון</th>
                  <th style="border: 1px solid #cbd5e1; padding: 10px; text-align: right;">כתובת</th>
                </tr>
              </thead>
              <tbody>
                ${classStudents.map((s, idx) => `
                  <tr>
                    <td style="border: 1px solid #cbd5e1; padding: 10px; text-align: center;">${idx + 1}</td>
                    <td style="border: 1px solid #cbd5e1; padding: 10px; font-weight: bold;">${s.fullName}</td>
                    <td style="border: 1px solid #cbd5e1; padding: 10px;">${s.nationalId}</td>
                    <td style="border: 1px solid #cbd5e1; padding: 10px; font-size: 11px;">
                      ${s.fatherPhone ? `אבא: ${s.fatherPhone}<br/>` : ''}
                      ${s.motherPhone ? `אמא: ${s.motherPhone}` : ''}
                    </td>
                    <td style="border: 1px solid #cbd5e1; padding: 10px; font-size: 11px;">${s.address || '-'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
      });

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('נא לאפשר חלונות קופצים בדפדפן כדי להדפיס');
        setIsPrintingList(false);
        return;
      }

      printWindow.document.write(`
        <html dir="rtl">
          <head>
            <title>רשימת תלמידים לפי כיתות</title>
            <style>
              body { font-family: sans-serif; margin: 0; padding: 0; }
            </style>
          </head>
          <body>
            ${pagesHtml}
            <script>
              window.onload = () => {
                setTimeout(() => { window.print(); window.close(); }, 600);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
      setIsPrintingList(false);
    }, 200);
  };

  const exportCardToPDF = async () => {
    const element = document.getElementById('printable-student-card');
    if (!element || !printingStudent) return;
    
    setActionLoading(true);
    try {
      const canvas = await safeHtml2Canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
        stripStylesheets: false
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.9);
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      const contentWidth = canvas.width;
      const contentHeight = canvas.height;
      
      // Calculate dimensions maintaining aspect ratio
      const pdfWidth = pageWidth - 20; // 10mm margins
      const pdfHeight = (contentHeight * pdfWidth) / contentWidth;
      
      pdf.addImage(imgData, 'JPEG', 10, 10, pdfWidth, Math.min(pdfHeight, pageHeight - 20));
      pdf.save(`כרטיס_תלמיד_${printingStudent.fullName.replace(/\s+/g, '_')}.pdf`);
    } catch (error) {
      console.error('PDF Export error:', error);
      alert('שגיאה ביצירת PDF. אנא נסה שנית.');
    } finally {
      setActionLoading(false);
    }
  };

  const exportListToPDF = async () => {
    if (filteredStudents.length === 0) {
      alert('אין תלמידים בסינון הנוכחי לייצוא');
      return;
    }

    setActionLoading(true);
    try {
      const activeCols = [
        { key: 'index' as const, label: '#' },
        { key: 'fullName' as const, label: 'שם מלא' },
        { key: 'nationalId' as const, label: 'תעודת זהות' },
        { key: 'className' as const, label: 'כיתה' },
        { key: 'transportName' as const, label: 'קו הסעה' },
        { key: 'address' as const, label: 'כתובת' },
        { key: 'fatherPhone' as const, label: 'טלפון אב' },
        { key: 'motherPhone' as const, label: 'טלפון אם' },
      ];

      const getVal = (s: Student, idx: number, key: string): string => {
        if (key === 'index') return `${idx + 1}`;
        if (key === 'fullName') return s.fullName || '-';
        if (key === 'nationalId') return s.nationalId || '-';
        if (key === 'className') return s.className || '-';
        if (key === 'transportName') return s.transportName || '-';
        if (key === 'address') return s.address || '-';
        if (key === 'fatherPhone') return s.fatherPhone || '-';
        if (key === 'motherPhone') return s.motherPhone || '-';
        return '-';
      };

      await generateStudentReportPDF(
        filteredStudents,
        activeCols,
        getVal as any,
        {
          reportTitle: 'רשימת תלמידים מרוכזת',
          yearName: currentYearObj?.name || selectedYear,
          filtersSummary: `סה״כ ${filteredStudents.length} תלמידים`,
          orientation: 'portrait',
          pageSize: 'a4'
        }
      );
    } catch (error) {
      console.warn('Fast PDF export error, falling back to print dialog:', error);
      handlePrintList();
    } finally {
      setActionLoading(false);
    }
  };

  const enrolledStudents = students.filter(s => {
    const enr = getStudentClassForYear(s, selectedYear);
    const enrollment = s.enrollments?.[selectedYear];
    const isInactive = enrollment?.active === false || s.active === false;
    return !!enr && !isInactive;
  });
  const unenrolledStudents = students.filter(s => {
    const enr = getStudentClassForYear(s, selectedYear);
    const enrollment = s.enrollments?.[selectedYear];
    const isInactive = enrollment?.active === false || s.active === false;
    return !enr && !isInactive;
  });
  const inactiveStudents = students.filter(s => {
    const enrollment = s.enrollments?.[selectedYear];
    return enrollment?.active === false || s.active === false;
  });

  const filteredStudents = students.filter(s => {
    const matchesSearch = s.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         s.nationalId.includes(searchTerm);
    const yearEnrollment = getStudentClassForYear(s, selectedYear);
    const enrollment = s.enrollments?.[selectedYear];
    const isInactive = enrollment?.active === false || s.active === false;
    
    // By default (showInactive === false), inactive students are hidden unless explicitly viewing the inactive tab
    if (!showInactive && isInactive && enrollmentStatusFilter !== 'inactive') {
      return false;
    }

    // Class filter matches against the student's class for the selected academic year
    const targetFilteredClass = classes.find(c => c.id === selectedClassFilter || c.name === selectedClassFilter);
    const matchesClass = selectedClassFilter === 'all' || 
      (yearEnrollment ? (
        yearEnrollment.classId === selectedClassFilter || 
        yearEnrollment.className === selectedClassFilter ||
        isSameClass(
          { id: yearEnrollment.classId, name: yearEnrollment.className },
          { id: targetFilteredClass?.id || selectedClassFilter, name: targetFilteredClass?.name || selectedClassFilter }
        )
      ) : false);

    const transportInfo = getStudentTransportForYear(s, selectedYear);
    const matchesTransport = selectedTransportFilter === 'all' || 
      (selectedTransportFilter === '__NONE__' ? !transportInfo.transportName : transportInfo.transportName === selectedTransportFilter);

    let matchesEnrollment = true;
    if (enrollmentStatusFilter === 'enrolled') {
      matchesEnrollment = !!yearEnrollment && !isInactive;
    } else if (enrollmentStatusFilter === 'unenrolled') {
      matchesEnrollment = !yearEnrollment && !isInactive;
    } else if (enrollmentStatusFilter === 'inactive') {
      matchesEnrollment = isInactive;
    }

    return matchesSearch && matchesClass && matchesTransport && matchesEnrollment;
  }).sort((a, b) => {
    let comparison = 0;
    if (sortField === 'fullName') {
      comparison = a.fullName.localeCompare(b.fullName, 'he', { sensitivity: 'base' });
    } else if (sortField === 'nationalId') {
      comparison = a.nationalId.localeCompare(b.nationalId, 'he', { numeric: true });
    } else if (sortField === 'academicYear') {
      const aEnrolled = !!(a.enrollments?.[selectedYear] && a.enrollments[selectedYear].active !== false);
      const bEnrolled = !!(b.enrollments?.[selectedYear] && b.enrollments[selectedYear].active !== false);
      if (aEnrolled === bEnrolled) {
        // secondary tie-breaker by total count of active enrolled years
        const aCount = Object.values(a.enrollments || {}).filter((e: any) => e && e.active !== false).length;
        const bCount = Object.values(b.enrollments || {}).filter((e: any) => e && e.active !== false).length;
        comparison = aCount - bCount;
      } else {
        comparison = aEnrolled ? 1 : -1;
      }
    } else if (sortField === 'status') {
      const aInactive = a.enrollments?.[selectedYear]?.active === false;
      const bInactive = b.enrollments?.[selectedYear]?.active === false;
      const aVal = aInactive ? 0 : 1;
      const bVal = bInactive ? 0 : 1;
      comparison = aVal - bVal;
    } else if (sortField === 'class') {
      const aClass = getStudentClassForYear(a, selectedYear)?.className || a.className || '';
      const bClass = getStudentClassForYear(b, selectedYear)?.className || b.className || '';
      comparison = aClass.localeCompare(bClass, 'he', { numeric: true });
    } else if (sortField === 'transport') {
      const aTrans = getStudentTransportForYear(a, selectedYear).transportName || '';
      const bTrans = getStudentTransportForYear(b, selectedYear).transportName || '';
      comparison = aTrans.localeCompare(bTrans, 'he');
    }

    if (comparison === 0) {
      comparison = a.fullName.localeCompare(b.fullName, 'he');
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const handleQuickExportExcel = () => {
    if (filteredStudents.length === 0) {
      alert('אין תלמידים בסינון הנוכחי לייצוא');
      return;
    }
    const data = filteredStudents.map((s, idx) => {
      const yearEnrollment = getStudentClassForYear(s, selectedYear);
      const transportInfo = getStudentTransportForYear(s, selectedYear);
      const rawEnrollment = s.enrollments?.[selectedYear];
      const isInactive = rawEnrollment?.active === false || s.active === false;
      return {
        '#': idx + 1,
        'שם מלא': s.fullName,
        'תעודת זהות': s.nationalId,
        'כיתה לשנה זו': yearEnrollment?.className || rawEnrollment?.className || s.className || 'לא שובץ/ה',
        'קו הסעה': transportInfo.transportName || 'ללא הסעה',
        'מלווה': transportInfo.escortName || '',
        'כתובת': s.address || '',
        'תאריך לידה': s.birthDate || '',
        'שם האב': s.fatherName || '',
        'טלפון אב': s.fatherPhone || '',
        'שם האם': s.motherName || '',
        'טלפון אם': s.motherPhone || '',
        'הערות בריאותיות': s.healthNotes || '',
        'סטטוס': isInactive ? 'לא פעיל/ה' : (yearEnrollment ? 'פעיל/ה משובץ/ת' : 'פעיל/ה טרם שובץ/ה')
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!views'] = [{ rightToLeft: true }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'רשימת תלמידים');
    const safeYear = currentYearObj.name.replace(/[\s\/\(\)]+/g, '_');
    XLSX.writeFile(wb, `רשימת_תלמידים_${safeYear}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="space-y-6" dir="rtl">
      {/* Print Overlay - only visible when printing */}
      <style>{`
        @media print {
          /* Global hide */
          body * {
            visibility: hidden !important;
          }
          
          /* Specialized show for Card */
          body.is-printing-card #printable-student-card,
          body.is-printing-card #printable-student-card * {
            visibility: visible !important;
          }
          
          /* Specialized show for List */
          body.is-printing-list #printable-student-list,
          body.is-printing-list #printable-student-list * {
            visibility: visible !important;
          }

          #printable-student-card,
          #printable-student-list {
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            height: auto !important;
            display: block !important;
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          
          /* Hide annoying elements */
          button, .no-print, [role="dialog"], .fixed, .backdrop-blur-sm {
            display: none !important;
          }
          .page-break-before-always {
            page-break-before: always !important;
            break-before: page !important;
          }
        }
      `}</style>

      {isPrintingList && (
        <div id="printable-student-list" className="fixed left-[-9999px] top-0 w-[800px] bg-white pointer-events-none">
          {Object.entries(
            filteredStudents.reduce((acc, s) => {
              if (!acc[s.className]) acc[s.className] = [];
              acc[s.className].push(s);
              return acc;
            }, {} as Record<string, Student[]>)
          ).sort(([a], [b]) => a.localeCompare(b)).map(([className, classStudents], index) => {
            const students = classStudents as Student[];
            return (
              <div key={className} id={`class-section-${index}`} className="p-8 bg-white border-b border-slate-100 last:border-0">
              <div className="flex justify-between items-center border-b-2 border-slate-200 pb-4 mb-6">
                <div>
                  <h2 className="text-2xl font-bold">רשימת תלמידים - כיתה {className}</h2>
                  <p className="text-slate-500">תאריך הדפסה: {new Date().toLocaleDateString('he-IL')}</p>
                </div>
                <img src="/logo.png" className="h-12 w-auto" alt="Logo" />
              </div>
              <table className="w-full border-collapse border border-slate-300 text-right text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-300 p-2 w-12 text-center">#</th>
                    <th className="border border-slate-300 p-2">שם מלא</th>
                    <th className="border border-slate-300 p-2">תעודת זהות</th>
                    <th className="border border-slate-300 p-2">כתובת</th>
                    <th className="border border-slate-300 p-2">טלפון הורים</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s, idx) => (
                    <tr key={s.id}>
                      <td className="border border-slate-300 p-2 text-center">{idx + 1}</td>
                      <td className="border border-slate-300 p-2 font-bold">{s.fullName}</td>
                      <td className="border border-slate-300 p-2 font-mono">{s.nationalId}</td>
                      <td className="border border-slate-300 p-2 text-xs">{s.address || '-'}</td>
                      <td className="border border-slate-300 p-2 text-xs">
                        {s.fatherPhone && `אבא: ${s.fatherPhone}`}<br/>
                        {s.motherPhone && `אמא: ${s.motherPhone}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            );
          })}
          <div className="mt-8 text-center text-[10px] text-slate-400 p-4">
            הודפס ממערכת ניהול אלמג'ד
          </div>
        </div>
      )}

      {printingStudent && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4 print:hidden">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl overflow-hidden"
          >
            <div id="printable-student-card" className="p-8">
              <div className="flex justify-between items-start border-b-2 border-slate-100 pb-6 mb-6">
                <div>
                  <h3 className="text-3xl font-bold text-slate-900">כרטיס תלמיד</h3>
                  <p className="text-indigo-600 font-bold mt-1 text-lg">{printingStudent.fullName}</p>
                </div>
                <div className="text-left">
                  <img 
                    src="/logo.png" 
                    className="h-16 w-auto mb-2 object-contain"
                    alt="Logo"
                  />
                  <p className="text-xs text-slate-400">הופק בתאריך: {new Date().toLocaleDateString('he-IL')}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">תעודת זהות</label>
                    <p className="text-lg font-mono text-slate-900">{printingStudent.nationalId}</p>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">תאריך לידה</label>
                    <p className="text-lg text-slate-900">{printingStudent.birthDate || 'לא הוזן'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">כיתה לשנת {currentYearObj.name}</label>
                    <p className="text-lg text-slate-900 font-bold text-indigo-700">
                      {getStudentClassForYear(printingStudent, selectedYear)?.className || printingStudent.className || 'לא שובץ/ה'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">כתובת</label>
                    <p className="text-lg text-slate-900">{printingStudent.address || 'לא הוזן'}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">שם האב וטלפון</label>
                    <p className="text-lg text-slate-900">{printingStudent.fatherName || '-'} {printingStudent.fatherPhone && `(${printingStudent.fatherPhone})`}</p>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">שם האם וטלפון</label>
                    <p className="text-lg text-slate-900">{printingStudent.motherName || '-'} {printingStudent.motherPhone && `(${printingStudent.motherPhone})`}</p>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">הסעה / מלווה</label>
                    <p className="text-lg text-slate-900">{printingStudent.transportName || '-'} {printingStudent.escortName && `/ ${printingStudent.escortName}`}</p>
                  </div>
                </div>

                <div className="col-span-2 mt-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">הערות בריאותיות</label>
                  <p className="text-slate-700 mt-1 whitespace-pre-wrap">{printingStudent.healthNotes || 'אין הערות מיוחדות'}</p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-6 flex flex-wrap justify-between items-center gap-3 border-t border-slate-100 print:hidden">
              <button
                type="button"
                onClick={() => {
                  const s = printingStudent;
                  setPrintingStudent(null);
                  openEditStudent(s);
                }}
                className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-5 py-3 rounded-xl font-bold flex items-center gap-2 transition-all border border-indigo-200"
              >
                <Edit2 size={18} />
                <span>עריכת פרטי תלמיד</span>
              </button>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setPrintingStudent(null)}
                  className="px-6 py-3 rounded-xl font-bold text-slate-600 hover:bg-slate-200 transition-all"
                >
                  סגור
                </button>
                <button
                  onClick={exportCardToPDF}
                  disabled={actionLoading}
                  className="bg-emerald-600 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 hover:bg-emerald-700 shadow-lg shadow-emerald-100 transition-all disabled:opacity-50"
                >
                  {actionLoading ? <Loader2 size={20} className="animate-spin" /> : <FileDown size={20} />}
                  <span>שמור כ-PDF</span>
                </button>
                <button
                  onClick={handlePrint}
                  className="bg-indigo-600 text-white px-8 py-3 rounded-xl font-bold flex items-center gap-2 hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all"
                >
                  <Printer size={20} />
                  <span>הדפס כרטיס</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">ניהול תלמידים</h2>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-slate-500">הוספה, עריכה וניהול של רשימת התלמידים</p>
            {lastUpdated && (
              <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                עודכן לאחרונה: {lastUpdated.toLocaleTimeString('he-IL')}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => fetchData(true)}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-white text-slate-600 border border-slate-200 px-4 py-3 rounded-2xl font-bold hover:bg-slate-50 transition-all shadow-sm disabled:opacity-50"
            title="רענן נתונים מהשרת"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            <span>רענן</span>
          </button>
          {students.length > 0 && (
            <button
              onClick={() => setShowDeleteAllConfirm(true)}
              className="inline-flex items-center gap-2 bg-red-50 text-red-600 px-6 py-3 rounded-2xl font-bold hover:bg-red-100 transition-all border border-red-100"
            >
              <Trash2 size={20} />
              <span>מחיקת כל התלמידים</span>
            </button>
          )}
          {students.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowAdvancedReportModal(true)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-3 rounded-2xl font-bold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg shadow-indigo-100"
                title="הפקת דוח PDF או אקסל לפי מסנן מתקדם"
              >
                <SlidersHorizontal size={20} />
                <span>הפקת דוח מתקדם (PDF/Excel)</span>
              </button>
              <button
                onClick={handleQuickExportExcel}
                className="inline-flex items-center gap-2 bg-white text-emerald-700 border border-emerald-200 px-4 py-3 rounded-2xl font-bold hover:bg-emerald-50 transition-all shadow-sm"
                title="ייצוא מהיר של רשימת התלמידים הנוכחית לקובץ Excel"
              >
                <FileSpreadsheet size={20} />
                <span>ייצוא לאקסל</span>
              </button>
              <button
                onClick={handlePrintList}
                className="inline-flex items-center gap-2 bg-white text-emerald-600 border border-emerald-100 px-5 py-3 rounded-2xl font-bold hover:bg-emerald-50 transition-all shadow-sm"
              >
                <Printer size={20} />
                <span>הדפסת רשימה</span>
              </button>
            </div>
          )}
          <button
            onClick={() => {
              setShowImportModal(true);
              setImportPreviewData([]);
              setImportErrorMsg('');
              setImportSuccessMsg('');
            }}
            className="inline-flex items-center gap-2 bg-emerald-600 text-white px-5 py-3 rounded-2xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-100"
            title="ייבוא תלמידים מקובץ Excel או CSV"
          >
            <FileSpreadsheet size={20} />
            <span>ייבוא מ-Excel</span>
          </button>
          <button
            onClick={() => {
              setIsAdding(true);
              setEditingId(null);
              setManualClassMode(false);
              setManualClassName('');
              setFormData({ 
                fullName: '', nationalId: '', birthDate: '', address: '', 
                fatherName: '', fatherPhone: '', motherName: '', motherPhone: '', 
                classId: '', transportName: '', escortName: '', healthNotes: '',
                enrollmentYear: selectedYear,
                isActive: true
              });
            }}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-2xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200"
          >
            <Plus size={20} />
            <span>הוספת תלמיד חדש</span>
          </button>
        </div>
      </div>

      {/* Academic Year Info Banner & Quick Filter Tabs */}
      <div className="bg-gradient-to-r from-indigo-500 via-indigo-600 to-purple-600 rounded-3xl p-6 text-white shadow-lg flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md">
            <Calendar size={28} className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-indigo-200 font-bold">שנת לימודים פעילה</span>
              <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-black">{currentYearObj.name}</span>
            </div>
            <h3 className="text-xl font-bold mt-0.5">ניהול ושיבוץ תלמידים</h3>
            <p className="text-xs text-indigo-100 mt-1">
              סה״כ {students.length} במאגר • {enrolledStudents.length} משובצים ({currentYearObj.name}) • {unenrolledStudents.length} טרם שובצו {inactiveStudents.length > 0 ? `• ${inactiveStudents.length} לא פעילים (${showInactive ? 'מוצגים כעת' : 'מוסתרים כברירת מחדל'})` : ''}
            </p>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex bg-black/20 p-1.5 rounded-2xl backdrop-blur-md text-xs font-bold shrink-0 self-start md:self-auto gap-1">
          <button
            onClick={() => setEnrollmentStatusFilter('all')}
            className={`px-3 py-2 rounded-xl transition-all ${
              enrollmentStatusFilter === 'all' 
                ? 'bg-white text-indigo-700 shadow-sm' 
                : 'text-white/80 hover:text-white'
            }`}
          >
            כל המאגר ({students.length})
          </button>
          <button
            onClick={() => setEnrollmentStatusFilter('enrolled')}
            className={`px-3 py-2 rounded-xl transition-all ${
              enrollmentStatusFilter === 'enrolled' 
                ? 'bg-white text-indigo-700 shadow-sm' 
                : 'text-white/80 hover:text-white'
            }`}
          >
            משובצים לשנה זו ({enrolledStudents.length})
          </button>
          <button
            onClick={() => setEnrollmentStatusFilter('unenrolled')}
            className={`px-3 py-2 rounded-xl transition-all ${
              enrollmentStatusFilter === 'unenrolled' 
                ? 'bg-amber-400 text-amber-950 shadow-sm' 
                : 'text-white/80 hover:text-white'
            }`}
          >
            טרם שובצו ({unenrolledStudents.length})
          </button>
          <button
            onClick={() => {
              setEnrollmentStatusFilter('inactive');
              setShowInactive(true);
            }}
            className={`px-3 py-2 rounded-xl transition-all ${
              enrollmentStatusFilter === 'inactive' 
                ? 'bg-red-400 text-red-950 shadow-sm' 
                : 'text-white/80 hover:text-white'
            }`}
          >
            לא פעילים לשנה זו ({inactiveStudents.length})
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-3xl shadow-sm border border-slate-100 flex flex-col md:flex-row items-center gap-4">
        <div className="flex items-center gap-3 flex-1 w-full">
          <Search className="text-slate-400" size={20} />
          <input
            type="text"
            placeholder="חיפוש לפי שם או תעודת זהות..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 outline-none text-slate-700 bg-transparent"
          />
        </div>
        <div className="flex items-center gap-3 w-full md:w-56 border-r md:border-r border-slate-100 pr-4">
          <School className="text-slate-400 shrink-0" size={18} />
          <select
            value={selectedClassFilter}
            onChange={(e) => setSelectedClassFilter(e.target.value)}
            className="flex-1 outline-none text-slate-700 bg-transparent appearance-none cursor-pointer font-bold text-xs"
          >
            <option value="all">כל הכיתות</option>
            {classes.map(c => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3 w-full md:w-56 border-r md:border-r border-slate-100 pr-4">
          <Bus className="text-slate-400 shrink-0" size={18} />
          <select
            value={selectedTransportFilter}
            onChange={(e) => setSelectedTransportFilter(e.target.value)}
            className="flex-1 outline-none text-slate-700 bg-transparent appearance-none cursor-pointer font-bold text-xs"
          >
            <option value="all">כל המסיעים / קווים</option>
            <option value="__NONE__">ללא הסעה / מסיע</option>
            {transportLines.map(line => (
              <option key={line} value={line}>{line}</option>
            ))}
          </select>
        </div>
        {/* Checkbox: Show / Hide Inactive Students (default unchecked / hidden) */}
        <div className="flex items-center w-full md:w-auto border-t md:border-t-0 md:border-r border-slate-100 pt-3 md:pt-0 md:pr-4 shrink-0">
          <label className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-2xl border transition-all cursor-pointer select-none ${
            showInactive 
              ? 'bg-red-50 border-red-200 text-red-800 shadow-sm' 
              : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300'
          }`}>
            <input
              type="checkbox"
              id="toggle-inactive-students"
              checked={showInactive}
              onChange={(e) => {
                const checked = e.target.checked;
                setShowInactive(checked);
                if (!checked && enrollmentStatusFilter === 'inactive') {
                  setEnrollmentStatusFilter('all');
                }
              }}
              className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600"
            />
            <span className="flex items-center gap-1.5 text-xs font-bold">
              <UserX size={16} className={showInactive ? 'text-red-500' : 'text-slate-400'} />
              <span>הצג תלמידים לא פעילים</span>
              {inactiveStudents.length > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                  showInactive ? 'bg-red-200 text-red-900' : 'bg-slate-200 text-slate-600'
                }`}>
                  {inactiveStudents.length}
                </span>
              )}
            </span>
          </label>
        </div>
        {/* Open Advanced Filter & Report Modal */}
        <div className="flex items-center w-full md:w-auto border-t md:border-t-0 md:border-r border-slate-100 pt-3 md:pt-0 md:pr-4 shrink-0">
          <button
            type="button"
            onClick={() => setShowAdvancedReportModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 hover:border-indigo-300 font-bold text-xs transition-all shadow-sm w-full md:w-auto justify-center"
            title="פתיחת מסננים מתקדמים והפקת דוחות PDF או אקסל"
          >
            <SlidersHorizontal size={16} className="text-indigo-600" />
            <span>מסנן מתקדם והפקת דוח</span>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showDeleteAllConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white p-8 rounded-3xl max-w-md w-full shadow-2xl text-center"
            >
              <div className="w-20 h-20 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Trash2 size={40} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">מחיקת כל התלמידים?</h3>
              <p className="text-slate-500 mb-8">פעולה זו תמחק את כל {students.length} התלמידים מהמערכת. לא ניתן לבטל פעולה זו.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteAllConfirm(false)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
                >
                  ביטול
                </button>
                <button
                  onClick={handleDeleteAllStudents}
                  disabled={actionLoading}
                  className="flex-1 bg-red-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-red-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-100"
                >
                  {actionLoading ? <Loader2 className="animate-spin" size={20} /> : <Trash2 size={20} />}
                  <span>מחק הכל</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {(isAdding || editingId) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white p-8 rounded-3xl border border-indigo-100 shadow-xl shadow-indigo-50/50"
          >
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2 text-indigo-900">
              <UserPlus size={24} />
              {editingId ? 'עריכת פרטי תלמיד' : 'הוספת תלמיד חדש'}
            </h3>
            <form onSubmit={editingId ? (e) => { e.preventDefault(); handleUpdateStudent(editingId); } : handleAddStudent} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">שם מלא</label>
                <div className="relative">
                  <Users className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    placeholder="שם מלא"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">תעודת זהות</label>
                <div className="relative">
                  <IdCard className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    required
                    value={formData.nationalId}
                    onChange={(e) => setFormData({ ...formData, nationalId: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-mono"
                    placeholder="9 ספרות"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <BirthDatePicker
                  label="תאריך לידה"
                  value={formData.birthDate}
                  onChange={(value) => setFormData({ ...formData, birthDate: value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">כתובת</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  placeholder="כתובת מגורים"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">שם אבא</label>
                <input
                  type="text"
                  value={formData.fatherName}
                  onChange={(e) => setFormData({ ...formData, fatherName: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">נייד אבא</label>
                <input
                  type="tel"
                  value={formData.fatherPhone}
                  onChange={(e) => setFormData({ ...formData, fatherPhone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">שם אמא</label>
                <input
                  type="text"
                  value={formData.motherName}
                  onChange={(e) => setFormData({ ...formData, motherName: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">נייד אמא</label>
                <input
                  type="tel"
                  value={formData.motherPhone}
                  onChange={(e) => setFormData({ ...formData, motherPhone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">שנת לימודים</label>
                <div className="relative">
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <select
                    value={formData.enrollmentYear || selectedYear}
                    onChange={(e) => {
                      const newYear = e.target.value;
                      const targetStudent = students.find(s => s.id === editingId);
                      const enrollmentForNewYear = targetStudent ? getStudentClassForYear(targetStudent, newYear) : null;
                      const rawEnrollment = targetStudent?.enrollments?.[newYear];
                      setFormData({
                        ...formData,
                        enrollmentYear: newYear,
                        classId: enrollmentForNewYear?.classId || formData.classId || '',
                        isActive: rawEnrollment ? rawEnrollment.active !== false : true
                      });
                    }}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none font-bold text-slate-800"
                  >
                    {academicYears.map(y => (
                      <option key={y.id} value={y.id}>{y.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">סטטוס לשנת {academicYears.find(y => y.id === (formData.enrollmentYear || selectedYear))?.name || (formData.enrollmentYear || selectedYear)}</label>
                <div className="relative">
                  <select
                    value={formData.isActive ? 'active' : 'inactive'}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
                    className={`w-full pr-10 pl-4 py-3 rounded-xl border outline-none transition-all font-bold appearance-none cursor-pointer ${
                      formData.isActive 
                        ? 'bg-emerald-50/60 border-emerald-300 text-emerald-800 focus:ring-2 focus:ring-emerald-400/30' 
                        : 'bg-red-50/60 border-red-300 text-red-800 focus:ring-2 focus:ring-red-400/30'
                    }`}
                  >
                    <option value="active">פעיל/ה</option>
                    <option value="inactive">לא פעיל/ה</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    {formData.isActive ? (
                      <UserCheck size={18} className="text-emerald-600" />
                    ) : (
                      <UserX size={18} className="text-red-600" />
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between mr-2">
                  <label className="text-sm font-bold text-slate-700">
                    שיוך לכיתה {formData.isActive ? '' : '(לא חובה עבור לא פעיל)'}
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const nextMode = !manualClassMode;
                      setManualClassMode(nextMode);
                      if (nextMode && formData.classId) {
                        const c = classes.find(cls => cls.id === formData.classId);
                        if (c) setManualClassName(c.name);
                      }
                    }}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-bold underline cursor-pointer"
                  >
                    {manualClassMode ? '← בחר מרשימת כיתות' : '✏️ הקלד כיתה ידנית'}
                  </button>
                </div>

                {manualClassMode ? (
                  <div className="relative">
                    <School className="absolute right-3 top-1/2 -translate-y-1/2 text-indigo-500" size={18} />
                    <input
                      type="text"
                      value={manualClassName}
                      onChange={(e) => setManualClassName(e.target.value)}
                      placeholder="הקלד שם כיתה (לדוגמה: גן שקד, כיתה א'1)..."
                      className="w-full pr-10 pl-4 py-3 rounded-xl bg-indigo-50/40 border border-indigo-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-bold text-indigo-950 placeholder:font-normal placeholder:text-slate-400"
                    />
                  </div>
                ) : (
                  <div className="relative">
                    <School className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <select
                      required={formData.isActive && !manualClassMode}
                      value={formData.classId}
                      onChange={(e) => {
                        if (e.target.value === '__MANUAL__') {
                          setManualClassMode(true);
                        } else {
                          setFormData({ ...formData, classId: e.target.value });
                        }
                      }}
                      className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none cursor-pointer"
                    >
                      <option value="">{formData.isActive ? 'בחר כיתה מהרשימה...' : 'ללא שיוך כיתתי (לא פעיל)'}</option>
                      {classes.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                      <option value="__MANUAL__" className="text-indigo-600 font-bold bg-indigo-50">
                        ➕ הקלדת כיתה באופן ידני...
                      </option>
                    </select>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">מסיע / קו</label>
                <div className="relative">
                  <Bus className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <select
                    value={formData.transportName}
                    onChange={(e) => setFormData({ ...formData, transportName: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none"
                  >
                    <option value="">בחר קו הסעה...</option>
                    {transportLines.map(line => (
                      <option key={line} value={line}>{line}</option>
                    ))}
                    <option value="אחר">אחר</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">מלווה</label>
                <input
                  type="text"
                  value={formData.escortName}
                  onChange={(e) => setFormData({ ...formData, escortName: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  placeholder="שם המלווה"
                />
              </div>
              <div className="space-y-2 md:col-span-2 lg:col-span-3 xl:col-span-4">
                <label className="text-sm font-bold text-slate-700 mr-2">הערות בריאותיות</label>
                <textarea
                  value={formData.healthNotes}
                  onChange={(e) => setFormData({ ...formData, healthNotes: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all h-24 resize-none"
                  placeholder="אלרגיות, תרופות, רגישויות..."
                />
              </div>
              <div className="md:col-span-2 lg:col-span-3 xl:col-span-4 flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => { 
                    setIsAdding(false); 
                    setEditingId(null); 
                    setManualClassMode(false); 
                    setManualClassName(''); 
                  }}
                  className="px-8 py-3 rounded-xl font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="bg-indigo-600 text-white px-10 py-3 rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center gap-2 shadow-lg shadow-indigo-100"
                >
                  {actionLoading ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
                  <span>{editingId ? 'עדכון פרטים' : 'שמירת תלמיד'}</span>
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Action Bar when items are selected */}
      <AnimatePresence>
        {selectedStudentIds.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-indigo-900 text-white p-4 rounded-2xl shadow-xl flex flex-wrap items-center justify-between gap-4 border border-indigo-700"
          >
            <div className="flex items-center gap-3">
              <span className="bg-indigo-600 text-white px-3 py-1 rounded-xl text-sm font-black">
                נבחרו {selectedStudentIds.length} תלמידים
              </span>
              <button
                onClick={() => setSelectedStudentIds([])}
                className="text-xs text-indigo-200 hover:text-white underline font-medium"
              >
                בטל בחירה
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => {
                  setBulkTargetYearId(selectedYear);
                  setShowBulkYearModal(true);
                }}
                disabled={actionLoading}
                className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
              >
                <Calendar size={16} />
                <span>שיוך לשנה״ל</span>
              </button>

              <button
                onClick={() => {
                  setBulkTargetClassId('');
                  setBulkManualClassName('');
                  setBulkManualMode(false);
                  setShowBulkClassModal(true);
                }}
                disabled={actionLoading}
                className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
              >
                <School size={16} />
                <span>שינוי כיתה גורף</span>
              </button>

              <button
                onClick={() => {
                  setBulkTargetTransportName('');
                  setBulkManualTransportName('');
                  setBulkManualTransportMode(false);
                  setShowBulkTransportModal(true);
                }}
                disabled={actionLoading}
                className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
              >
                <Bus size={16} />
                <span>שיוך למסיע / קו</span>
              </button>

              <button
                onClick={() => handleBulkStatusChange(true)}
                disabled={actionLoading}
                className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
              >
                <UserCheck size={16} />
                <span>הגדר כפעילים לשנה זו</span>
              </button>

              <button
                onClick={() => handleBulkStatusChange(false)}
                disabled={actionLoading}
                className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
              >
                <UserX size={16} />
                <span>הגדר כלא פעילים</span>
              </button>

              <button
                onClick={() => setShowAdvancedReportModal(true)}
                disabled={actionLoading}
                className="bg-indigo-700 hover:bg-indigo-600 text-white px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm border border-indigo-400"
                title="הפקת דוח PDF או אקסל עבור התלמידים שנבחרו"
              >
                <FileSpreadsheet size={16} />
                <span>הפקת דוח לנבחרים (PDF/Excel)</span>
              </button>

              <button
                onClick={() => setShowBulkDeleteModal(true)}
                disabled={actionLoading}
                className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
              >
                <Trash2 size={16} />
                <span>מחיקת הנבחרים</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active filters & count summary bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-2 text-xs text-slate-500">
        <div className="flex flex-wrap items-center gap-2 font-medium">
          <span>
            מוצגים <strong className="text-slate-900 font-bold">{filteredStudents.length}</strong> תלמידים
          </span>
          {!showInactive && inactiveStudents.length > 0 && enrollmentStatusFilter !== 'inactive' && (
            <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 px-2.5 py-1 rounded-xl text-[11px] font-semibold border border-slate-200">
              <UserX size={13} className="text-slate-400" />
              <span>{inactiveStudents.length} תלמידים לא פעילים מוסתרים (ברירת מחדל)</span>
              <button 
                type="button"
                onClick={() => setShowInactive(true)}
                className="text-indigo-600 hover:text-indigo-800 underline font-bold cursor-pointer mr-1"
                title="סמן ב-V להצגת תלמידים לא פעילים"
              >
                הצג אותם
              </button>
            </span>
          )}
          {showInactive && inactiveStudents.length > 0 && (
            <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 px-2.5 py-1 rounded-xl text-[11px] font-semibold border border-red-200">
              <UserX size={13} className="text-red-500" />
              <span>כולל {inactiveStudents.length} תלמידים לא פעילים</span>
              <button 
                type="button"
                onClick={() => {
                  setShowInactive(false);
                  if (enrollmentStatusFilter === 'inactive') setEnrollmentStatusFilter('all');
                }}
                className="text-red-700 hover:text-red-900 underline font-bold cursor-pointer mr-1"
                title="בטל סימון V להסתרת תלמידים לא פעילים"
              >
                הסתר
              </button>
            </span>
          )}
        </div>
        {(searchTerm || selectedClassFilter !== 'all' || selectedTransportFilter !== 'all' || enrollmentStatusFilter !== 'all' || showInactive) && (
          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              setSelectedClassFilter('all');
              setSelectedTransportFilter('all');
              setEnrollmentStatusFilter('all');
              setShowInactive(false);
            }}
            className="text-indigo-600 hover:text-indigo-800 hover:underline font-bold text-xs cursor-pointer"
          >
            איפוס כל המסננים
          </button>
        )}
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-20 flex flex-col items-center justify-center text-slate-400">
            <Loader2 className="animate-spin mb-4" size={40} />
            <p className="font-medium">טוען רשימת תלמידים...</p>
          </div>
        ) : filteredStudents.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-right">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-4 py-4 w-12 text-center">
                    <button
                      type="button"
                      onClick={handleSelectAllFiltered}
                      className="p-1 text-slate-400 hover:text-indigo-600 transition-colors"
                      title={
                        filteredStudents.length > 0 && filteredStudents.every(s => selectedStudentIds.includes(s.id))
                          ? 'בטל בחירת הכל'
                          : 'בחר הכל במסנן הנוכחי'
                      }
                    >
                      {filteredStudents.length > 0 && filteredStudents.every(s => selectedStudentIds.includes(s.id)) ? (
                        <CheckSquare size={20} className="text-indigo-600" />
                      ) : selectedStudentIds.some(id => filteredStudents.some(s => s.id === id)) ? (
                        <MinusSquare size={20} className="text-indigo-400" />
                      ) : (
                        <Square size={20} />
                      )}
                    </button>
                  </th>
                  <th 
                    onClick={() => handleSort('fullName')}
                    className="px-6 py-4 font-bold text-slate-700 text-sm cursor-pointer select-none hover:bg-slate-100 transition-colors"
                    title="לחץ למיון לפי שם מלא"
                  >
                    <div className="flex items-center gap-1.5">
                      <span>שם מלא</span>
                      {sortField === 'fullName' ? (
                        sortDirection === 'asc' ? <ArrowUp size={15} className="text-indigo-600 shrink-0" /> : <ArrowDown size={15} className="text-indigo-600 shrink-0" />
                      ) : (
                        <ArrowUpDown size={14} className="text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('nationalId')}
                    className="px-6 py-4 font-bold text-slate-700 text-sm cursor-pointer select-none hover:bg-slate-100 transition-colors"
                    title="לחץ למיון לפי תעודת זהות"
                  >
                    <div className="flex items-center gap-1.5">
                      <span>תעודת זהות</span>
                      {sortField === 'nationalId' ? (
                        sortDirection === 'asc' ? <ArrowUp size={15} className="text-indigo-600 shrink-0" /> : <ArrowDown size={15} className="text-indigo-600 shrink-0" />
                      ) : (
                        <ArrowUpDown size={14} className="text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
                      )}
                    </div>
                  </th>
                  {enrollmentStatusFilter === 'all' && (
                    <th 
                      onClick={() => handleSort('academicYear')}
                      className="px-6 py-4 font-bold text-slate-700 text-sm cursor-pointer select-none hover:bg-slate-100 transition-colors"
                      title="לחץ למיון לפי שיוך לשנה״ל"
                    >
                      <div className="flex items-center gap-1.5">
                        <span>שייך לשנה״ל</span>
                        {sortField === 'academicYear' ? (
                          sortDirection === 'asc' ? <ArrowUp size={15} className="text-indigo-600 shrink-0" /> : <ArrowDown size={15} className="text-indigo-600 shrink-0" />
                        ) : (
                          <ArrowUpDown size={14} className="text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
                        )}
                      </div>
                    </th>
                  )}
                  <th 
                    onClick={() => handleSort('status')}
                    className="px-6 py-4 font-bold text-slate-700 text-sm cursor-pointer select-none hover:bg-slate-100 transition-colors"
                    title={`לחץ למיון לפי סטטוס לשנת ${currentYearObj.name}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span>סטטוס ({currentYearObj.name})</span>
                      {sortField === 'status' ? (
                        sortDirection === 'asc' ? <ArrowUp size={15} className="text-indigo-600 shrink-0" /> : <ArrowDown size={15} className="text-indigo-600 shrink-0" />
                      ) : (
                        <ArrowUpDown size={14} className="text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('class')}
                    className="px-6 py-4 font-bold text-slate-700 text-sm cursor-pointer select-none hover:bg-slate-100 transition-colors"
                    title="לחץ למיון לפי כיתה"
                  >
                    <div className="flex items-center gap-1.5">
                      <span>כיתה</span>
                      {sortField === 'class' ? (
                        sortDirection === 'asc' ? <ArrowUp size={15} className="text-indigo-600 shrink-0" /> : <ArrowDown size={15} className="text-indigo-600 shrink-0" />
                      ) : (
                        <ArrowUpDown size={14} className="text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('transport')}
                    className="px-6 py-4 font-bold text-slate-700 text-sm cursor-pointer select-none hover:bg-slate-100 transition-colors"
                    title="לחץ למיון לפי מסיע"
                  >
                    <div className="flex items-center gap-1.5">
                      <span>מסיע</span>
                      {sortField === 'transport' ? (
                        sortDirection === 'asc' ? <ArrowUp size={15} className="text-indigo-600 shrink-0" /> : <ArrowDown size={15} className="text-indigo-600 shrink-0" />
                      ) : (
                        <ArrowUpDown size={14} className="text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 font-bold text-slate-700 text-sm text-center">פעולות</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredStudents.map((s) => {
                  const enrollment = getStudentClassForYear(s, selectedYear);
                  const rawEnrollment = s.enrollments?.[selectedYear];
                  const isInactive = rawEnrollment?.active === false;
                  const isSelected = selectedStudentIds.includes(s.id);
                  const isSaving = !!savingStudentIds[s.id];
                  const transportInfo = getStudentTransportForYear(s, selectedYear);

                  return (
                    <tr 
                      key={s.id} 
                      className={`hover:bg-slate-50/50 transition-colors ${
                        isSelected ? 'bg-indigo-50/40' : isInactive ? 'bg-slate-50/40 opacity-75' : ''
                      }`}
                    >
                      <td className="px-4 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectStudent(s.id)}
                          className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600"
                        />
                      </td>
                      <td className="px-6 py-4 font-medium text-slate-900">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setPrintingStudent(s)}
                            className="font-bold text-slate-900 hover:text-indigo-600 hover:underline transition-colors text-right flex items-center gap-1.5 group cursor-pointer"
                            title="לחץ לצפייה בדף פרטי התלמיד והדפסה"
                          >
                            <span>{s.fullName}</span>
                          </button>
                          {isSaving && (
                            <Loader2 size={14} className="animate-spin text-indigo-500 shrink-0" title="שומר שינויים..." />
                          )}
                          {isInactive && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-bold">לא פעיל</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-mono text-slate-500 text-sm">{s.nationalId}</td>
                      {enrollmentStatusFilter === 'all' && (
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1.5 min-w-[170px]">
                            <div className="flex flex-wrap items-center gap-1">
                              {academicYears.filter(y => s.enrollments?.[y.id] && s.enrollments[y.id].active !== false).map(y => (
                                <span 
                                  key={y.id} 
                                  className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200"
                                  title={`משויך ופעיל בשנת ${y.name}`}
                                >
                                  <Calendar size={10} className="text-indigo-500" />
                                  <span>{y.name.split(' ')[0]}</span>
                                </span>
                              ))}
                              {!academicYears.some(y => s.enrollments?.[y.id] && s.enrollments[y.id].active !== false) && (
                                <span className="text-[11px] text-amber-600 font-medium">לא משויך לאף שנה</span>
                              )}
                            </div>

                            <select
                              value=""
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleAssignStudentToYear(s, e.target.value);
                                }
                              }}
                              disabled={isSaving || actionLoading}
                              className="w-full text-xs bg-white border border-slate-300 hover:border-indigo-400 focus:border-indigo-500 rounded-lg py-1.5 px-2 text-slate-800 outline-none focus:ring-2 focus:ring-indigo-100 font-bold transition-all shadow-xs cursor-pointer"
                            >
                              <option value="">שייך לשנה״ל...</option>
                              {academicYears.map(y => {
                                const isEnrolled = s.enrollments?.[y.id] && s.enrollments[y.id].active !== false;
                                return (
                                  <option key={y.id} value={y.id}>
                                    {y.name} {isEnrolled ? '✓ (משויך)' : ''}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <select
                          value={isInactive ? 'inactive' : 'active'}
                          onChange={(e) => handleStatusChange(s, e.target.value)}
                          disabled={isSaving || actionLoading}
                          className={`text-xs font-bold rounded-lg px-2.5 py-1.5 outline-none border transition-all cursor-pointer ${
                            isInactive
                              ? 'bg-red-50 text-red-700 border-red-200 focus:ring-2 focus:ring-red-200'
                              : 'bg-emerald-50 text-emerald-700 border-emerald-200 focus:ring-2 focus:ring-emerald-200'
                          }`}
                        >
                          <option value="active" className="bg-white text-emerald-800 font-bold">פעיל</option>
                          <option value="inactive" className="bg-white text-red-800 font-bold">לא פעיל</option>
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <div className="min-w-[170px]">
                          <select
                            value={
                              enrollment?.classId && classes.some(c => c.id === enrollment.classId)
                                ? enrollment.classId
                                : enrollment?.className && classes.find(c => isSameClass({ name: c.name }, { name: enrollment.className }))
                                  ? classes.find(c => isSameClass({ name: c.name }, { name: enrollment.className }))!.id
                                  : enrollment?.classId ||
                                    (enrollment?.className ? '__CUSTOM_ENROLLED__' : '')
                            }
                            onChange={(e) => handleClassChangeInTable(s, e.target.value)}
                            disabled={isSaving || actionLoading}
                            title="בחר כיתה עבור התלמיד לשנת הלימודים"
                            className={`w-full text-xs font-bold rounded-lg px-2.5 py-1.5 outline-none border transition-all cursor-pointer ${
                              isInactive
                                ? 'bg-slate-50 text-slate-500 border-slate-200 hover:border-slate-300'
                                : enrollment?.className
                                  ? 'bg-indigo-50/80 text-indigo-800 border-indigo-200 hover:border-indigo-400 focus:ring-2 focus:ring-indigo-300'
                                  : 'bg-amber-50 text-amber-800 border-amber-200 hover:border-amber-400 focus:ring-2 focus:ring-amber-300'
                            }`}
                          >
                            <option value="__NONE__">
                              {isInactive ? 'ללא שיוך (לא פעיל)' : 'בחר כיתה / ללא שיוך'}
                            </option>
                            {classes.map(c => (
                              <option key={c.id} value={c.id} className="text-slate-800 font-bold">
                                {c.name}
                              </option>
                            ))}
                            {enrollment?.className && !classes.some(c => isSameClass({ name: c.name }, { name: enrollment.className })) && (
                              <option value="__CUSTOM_ENROLLED__" className="text-slate-800 font-bold">
                                {enrollment.className} (נוכחי)
                              </option>
                            )}
                            {!enrollment?.className && rawEnrollment?.className && !classes.some(c => isSameClass({ name: c.name }, { name: rawEnrollment.className })) && (
                              <option value="__CUSTOM_RAW__" className="text-slate-800 font-bold">
                                {rawEnrollment.className} (עבר)
                              </option>
                            )}
                            <option value="__MANUAL__" className="text-indigo-600 font-bold bg-indigo-50">
                              ➕ הקלדת כיתה ידנית...
                            </option>
                          </select>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="min-w-[150px]">
                          <select
                            value={transportInfo.transportName || '__NONE__'}
                            onChange={(e) => handleTransportChangeInTable(s, e.target.value)}
                            disabled={isSaving || actionLoading}
                            title="בחר קו הסעה / מסיע לשנת הלימודים"
                            className={`w-full text-xs font-bold rounded-lg px-2.5 py-1.5 outline-none border transition-all cursor-pointer ${
                              transportInfo.transportName
                                ? 'bg-amber-50/80 text-amber-900 border-amber-200 hover:border-amber-400 focus:ring-2 focus:ring-amber-300'
                                : 'bg-slate-50 text-slate-400 border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <option value="__NONE__">ללא הסעה / מסיע</option>
                            {transportLines.map(line => (
                              <option key={line} value={line} className="text-slate-800 font-bold">
                                {line}
                              </option>
                            ))}
                            {transportInfo.transportName && !transportLines.includes(transportInfo.transportName) && (
                              <option value={transportInfo.transportName} className="text-slate-800 font-bold">
                                {transportInfo.transportName}
                              </option>
                            )}
                            <option value="__MANUAL__" className="text-amber-800 font-bold bg-amber-50">
                              ➕ הקלדת מסיע ידני...
                            </option>
                          </select>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setPrintingStudent(s)}
                            className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-xl transition-all"
                            title="הדפסת פרטי תלמיד"
                          >
                            <Printer size={18} />
                          </button>
                          <button
                            onClick={() => openEditStudent(s)}
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                            title="עריכה"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteStudent(s.id)}
                            disabled={isSaving || actionLoading}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all disabled:opacity-40"
                            title="מחיקה"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-20 flex flex-col items-center justify-center text-slate-400">
            <div className="p-5 bg-slate-50 rounded-full mb-4">
              <Users size={40} />
            </div>
            <p className="font-medium text-lg text-slate-700">לא נמצאו תלמידים</p>
            {!showInactive && inactiveStudents.length > 0 && (
              <div className="mt-3 flex flex-col items-center gap-2">
                <p className="text-xs text-slate-500">
                  ישנם {inactiveStudents.length} תלמידים לא פעילים שמוסתרים כברירת מחדל.
                </p>
                <button
                  type="button"
                  onClick={() => setShowInactive(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-xl text-xs font-bold transition-all border border-indigo-100"
                >
                  <UserX size={14} className="text-red-500" />
                  <span>סמן להצגת {inactiveStudents.length} התלמידים הלא פעילים</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk Change Class Modal */}
      <AnimatePresence>
        {showBulkClassModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white p-6 sm:p-8 rounded-3xl max-w-lg w-full shadow-2xl text-right"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
                  <School size={26} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">שינוי כיתה גורף</h3>
                  <p className="text-xs text-slate-500">עבור {selectedStudentIds.length} תלמידים שנבחרו לשנת {currentYearObj.name}</p>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <label className="text-sm font-bold text-slate-700 block">בחר את כיתת היעד:</label>

                {!bulkManualMode ? (
                  <select
                    value={bulkTargetClassId}
                    onChange={(e) => {
                      if (e.target.value === '__MANUAL__') {
                        setBulkManualMode(true);
                      } else {
                        setBulkTargetClassId(e.target.value);
                      }
                    }}
                    className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none text-slate-800 font-bold"
                  >
                    <option value="">-- בחר כיתה --</option>
                    <option value="__NONE__">ללא כיתה (ביטול שיוך)</option>
                    {classes.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                    <option value="__MANUAL__" className="text-indigo-600 font-bold bg-indigo-50">
                      ➕ הקלדת כיתה חדשה...
                    </option>
                  </select>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="הקלד שם כיתה חדשה..."
                      value={bulkManualClassName}
                      onChange={(e) => setBulkManualClassName(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none text-slate-800 font-bold"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setBulkManualMode(false)}
                      className="text-xs text-indigo-600 hover:underline font-bold"
                    >
                      ← חזרה לבחירה מרשימת הכיתות
                    </button>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowBulkClassModal(false)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
                >
                  ביטול
                </button>
                <button
                  type="button"
                  onClick={handleBulkClassApply}
                  disabled={actionLoading || (!bulkTargetClassId && !bulkManualClassName.trim())}
                  className="flex-1 bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-100"
                >
                  {actionLoading ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
                  <span>החל שינוי</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Change Transport Modal */}
      <AnimatePresence>
        {showBulkTransportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white p-6 sm:p-8 rounded-3xl max-w-lg w-full shadow-2xl text-right"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-amber-50 text-amber-600 rounded-2xl">
                  <Bus size={26} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">שיוך גורף למסיע / קו הסעה</h3>
                  <p className="text-xs text-slate-500">עבור {selectedStudentIds.length} תלמידים שנבחרו לשנת {currentYearObj.name}</p>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <label className="text-sm font-bold text-slate-700 block">בחר קו הסעה / מסיע:</label>

                {!bulkManualTransportMode ? (
                  <select
                    value={bulkTargetTransportName}
                    onChange={(e) => {
                      if (e.target.value === '__MANUAL__') {
                        setBulkManualTransportMode(true);
                      } else {
                        setBulkTargetTransportName(e.target.value);
                      }
                    }}
                    className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-amber-500 outline-none text-slate-800 font-bold"
                  >
                    <option value="">-- בחר מסיע / קו --</option>
                    <option value="__NONE__">ללא הסעה (ביטול שיוך להסעה)</option>
                    {transportLines.map(line => (
                      <option key={line} value={line}>{line}</option>
                    ))}
                    <option value="__MANUAL__" className="text-amber-700 font-bold bg-amber-50">
                      ➕ הקלדת קו / מסיע חדש...
                    </option>
                  </select>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="הקלד שם קו הסעה / מסיע חדש..."
                      value={bulkManualTransportName}
                      onChange={(e) => setBulkManualTransportName(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-amber-500 outline-none text-slate-800 font-bold"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setBulkManualTransportMode(false)}
                      className="text-xs text-amber-600 hover:underline font-bold"
                    >
                      ← חזרה לבחירה מרשימת המסיעים
                    </button>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowBulkTransportModal(false)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
                >
                  ביטול
                </button>
                <button
                  type="button"
                  onClick={handleBulkTransportApply}
                  disabled={actionLoading || (!bulkTargetTransportName && !bulkManualTransportName.trim())}
                  className="flex-1 bg-amber-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-amber-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-100"
                >
                  {actionLoading ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
                  <span>החל שינוי</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Assign to Year Modal */}
      <AnimatePresence>
        {showBulkYearModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white p-6 sm:p-8 rounded-3xl max-w-lg w-full shadow-2xl text-right"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-purple-50 text-purple-600 rounded-2xl">
                  <Calendar size={26} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">שיוך לשנת לימודים (שנה״ל)</h3>
                  <p className="text-xs text-slate-500">שיוך {selectedStudentIds.length} תלמידים שנבחרו לשנת לימודים מסוימת</p>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <label className="text-sm font-bold text-slate-700 block">בחר את שנת הלימודים ליעד:</label>
                <select
                  value={bulkTargetYearId}
                  onChange={(e) => setBulkTargetYearId(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-purple-500 outline-none text-slate-800 font-bold"
                >
                  <option value="">-- בחר שנת לימודים --</option>
                  {academicYears.map(y => (
                    <option key={y.id} value={y.id}>
                      {y.name} {y.isDefault || y.id === selectedYear ? '(שנה נוכחית)' : ''}
                    </option>
                  ))}
                </select>

                <div className="p-4 bg-purple-50/70 border border-purple-100 rounded-2xl text-xs text-purple-900 leading-relaxed space-y-1">
                  <p className="font-bold flex items-center gap-1.5">
                    <span>💡</span>
                    <span>כיצד פועל השיוך:</span>
                  </p>
                  <p>• התלמידים שנבחרו ישוייכו ויוגדרו כפעילים בשנת הלימודים שנבחרה.</p>
                  <p>• אם לתלמיד יש שיבוץ כיתה קיים, הוא יישמר גם בשנה הנבחרת או שניתן לעדכנו לאחר מכן.</p>
                  <p>• נתוני השנים הקודמות נשמרים באופן מלא ללא שום פגיעה.</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowBulkYearModal(false)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
                >
                  ביטול
                </button>
                <button
                  type="button"
                  onClick={handleBulkYearAssign}
                  disabled={actionLoading || !bulkTargetYearId}
                  className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-purple-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-100"
                >
                  {actionLoading ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
                  <span>בצע שיוך לשנה״ל</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Delete Modal */}
      <AnimatePresence>
        {showBulkDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white p-8 rounded-3xl max-w-md w-full shadow-2xl text-center"
            >
              <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">מחיקת {selectedStudentIds.length} תלמידים?</h3>
              <p className="text-slate-500 text-sm mb-6">פעולה זו תמחק לצמיתות את התלמידים שנבחרו. לא ניתן לבטל פעולה זו.</p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBulkDeleteModal(false)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
                >
                  ביטול
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={actionLoading}
                  className="flex-1 bg-red-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-red-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-100"
                >
                  {actionLoading ? <Loader2 className="animate-spin" size={20} /> : <Trash2 size={20} />}
                  <span>אישור מחיקה</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Excel / CSV Import Modal */}
      <AnimatePresence>
        {showImportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl max-w-4xl w-full shadow-2xl overflow-hidden text-right flex flex-col max-h-[90vh]"
            >
              {/* Header */}
              <div className="p-6 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-emerald-600 text-white rounded-2xl">
                    <FileSpreadsheet size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">ייבוא תלמידים מקובץ Excel</h3>
                    <p className="text-xs text-slate-500">קליטה מהירה של תלמידים, עדכון פרטים ושיבוץ לכיתות</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowImportModal(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-200 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-6 overflow-y-auto flex-1">
                {/* Year Selection for Import */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-2xl bg-indigo-50 border border-indigo-100">
                  <div className="flex items-center gap-2">
                    <Calendar className="text-indigo-600" size={20} />
                    <span className="text-sm font-bold text-indigo-900">שנת לימודים לקליטת התלמידים:</span>
                  </div>
                  <select
                    value={importTargetYear}
                    onChange={(e) => setImportTargetYear(e.target.value)}
                    className="bg-white border border-indigo-200 rounded-xl px-4 py-2 font-bold text-sm text-indigo-950 outline-none"
                  >
                    {academicYears.map(y => (
                      <option key={y.id} value={y.id}>{y.name}</option>
                    ))}
                  </select>
                </div>

                {/* Template Download Section */}
                <div className="p-4 rounded-2xl bg-emerald-50/60 border border-emerald-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <h4 className="text-sm font-bold text-emerald-950 flex items-center gap-2">
                      <Download size={16} className="text-emerald-700" />
                      הורד תבנית Excel לדוגמה
                    </h4>
                    <p className="text-xs text-emerald-800">
                      התבנית כוללת את שמות העמודות הנדרשות (שם מלא, ת"ז, כיתה, טלפונים, הסעה ועוד)
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={downloadExcelTemplate}
                    className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-bold text-xs shadow-md shadow-emerald-100 transition-all shrink-0"
                  >
                    <Download size={16} />
                    <span>הורדת תבנית Excel (.xlsx)</span>
                  </button>
                </div>

                {/* Upload Input Area */}
                <div className="border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center hover:border-emerald-400 transition-colors bg-slate-50/50">
                  <input
                    type="file"
                    id="excel-file-input"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleExcelFileUpload}
                    className="hidden"
                  />
                  <label htmlFor="excel-file-input" className="cursor-pointer flex flex-col items-center gap-3">
                    <div className="p-4 bg-emerald-100 text-emerald-700 rounded-full">
                      <Upload size={28} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-800">לחץ לבחירת קובץ Excel או גרור לכאן</p>
                      <p className="text-xs text-slate-400 mt-1">תומך בקבצי XLSX, XLS ו-CSV</p>
                    </div>
                  </label>
                </div>

                {/* Loading Indicator */}
                {importLoading && (
                  <div className="flex items-center justify-center p-6 gap-3 text-indigo-600 font-bold">
                    <Loader2 className="animate-spin" size={24} />
                    <span>מעבד את הנתונים...</span>
                  </div>
                )}

                {/* Error message */}
                {importErrorMsg && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-sm font-bold flex items-center gap-2">
                    <AlertCircle size={20} />
                    <span>{importErrorMsg}</span>
                  </div>
                )}

                {/* Success message */}
                {importSuccessMsg && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-emerald-800 text-sm font-bold flex items-center gap-2">
                    <Check size={20} />
                    <span>{importSuccessMsg}</span>
                  </div>
                )}

                {/* Preview Table */}
                {importPreviewData.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        <Info size={16} className="text-indigo-600" />
                        <span>נמצאו {importPreviewData.length} תלמידים בקובץ:</span>
                      </h4>
                      <span className="text-xs text-slate-400">מציג עד 10 שורות לדוגמה</span>
                    </div>

                    <div className="overflow-x-auto border border-slate-100 rounded-2xl">
                      <table className="w-full text-right text-xs">
                        <thead className="bg-slate-50 border-b border-slate-100 font-bold text-slate-700">
                          <tr>
                            <th className="p-3">#</th>
                            <th className="p-3">שם מלא</th>
                            <th className="p-3">ת"ז</th>
                            <th className="p-3">כיתה</th>
                            <th className="p-3">טלפון הורים</th>
                            <th className="p-3">הסעה</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {importPreviewData.slice(0, 10).map((row, idx) => (
                            <tr key={idx} className="hover:bg-slate-50/50">
                              <td className="p-3 font-mono text-slate-400">{idx + 1}</td>
                              <td className="p-3 font-bold text-slate-900">{row.fullName}</td>
                              <td className="p-3 font-mono">{row.nationalId || '-'}</td>
                              <td className="p-3">
                                <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded font-bold">
                                  {row.className || 'ללא כיתה'}
                                </span>
                              </td>
                              <td className="p-3 text-slate-500">
                                {row.fatherPhone || row.motherPhone || '-'}
                              </td>
                              <td className="p-3 text-slate-500">{row.transportName || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  className="px-6 py-2.5 rounded-xl font-bold border border-slate-200 text-slate-600 hover:bg-slate-100 transition-all text-sm"
                >
                  סגור
                </button>
                {importPreviewData.length > 0 && (
                  <button
                    type="button"
                    onClick={handleExecuteImport}
                    disabled={importLoading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 shadow-lg shadow-emerald-100 disabled:opacity-50 text-sm"
                  >
                    {importLoading ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
                    <span>קלוט וייבא {importPreviewData.length} תלמידים</span>
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Student Advanced Report & Filter Modal (PDF / Excel) */}
      <StudentAdvancedReportModal
        isOpen={showAdvancedReportModal}
        onClose={() => setShowAdvancedReportModal(false)}
        students={students}
        classes={classes}
        transportLines={transportLines}
        academicYears={academicYears}
        selectedYear={selectedYear}
        currentYearName={currentYearObj.name}
        preSelectedStudentIds={selectedStudentIds}
        initialSearch={searchTerm}
        initialClassFilter={selectedClassFilter}
        initialTransportFilter={selectedTransportFilter}
      />
    </div>
  );
};

export default StudentManagement;
